/* global $ */
import Sidebar from "@/components/Home_Sidebar/sidebarView.vue";
import Navbar from "@/components/Home_Navbar/NavbarView.vue";
import { csrf_headers, get_token, getCookie } from '@/router/index.js';
import FilePreviewComponent from "@/components/FilePreviewComponent/FilePreviewComponent.vue";
import EmojiPicker from 'vue3-emoji-picker'
import 'vue3-emoji-picker/css'
import OneChatWebSocketClient from "@/services/one_chat_websocket";

const MAX_SOCKET_ATTACHMENT_BYTES = 35 * 1024 * 1024;
const ACTIVE_MESSAGE_REFRESH_MS = 4000;
const IGNORED_CHAT_SOCKET_EVENTS = new Set([
  'connection.accepted',
  'conversation.subscribed',
]);
const CHAT_SOCKET_EVENT_HANDLERS = new Map([
  ['conversation.created', (vm, event) => vm.handle_socket_conversation_created(event)],
  ['message.created', (vm, event) => vm.handle_socket_message_created(event.message)],
  ['message.deleted', (vm, event) => vm.handle_socket_message_deleted(event)],
  ['message.react', (vm, event) => vm.handle_socket_message_reacted(event)],
  ['message.reaction', (vm, event) => vm.handle_socket_message_reacted(event)],
  ['message.reactions', (vm, event) => vm.handle_socket_message_reacted(event)],
  ['message.reacted', (vm, event) => vm.handle_socket_message_reacted(event)],
  ['message.reaction.updated', (vm, event) => vm.handle_socket_message_reacted(event)],
  ['message.edit', (vm, event) => vm.handle_socket_message_updated(event)],
  ['message.edited', (vm, event) => vm.handle_socket_message_updated(event)],
  ['message.update', (vm, event) => vm.handle_socket_message_updated(event)],
  ['message.updated', (vm, event) => vm.handle_socket_message_updated(event)],
  ['message.pinned', (vm, event) => vm.handle_socket_message_pinned(event)],
  ['message.unpinned', (vm, event) => vm.handle_socket_message_pinned(event)],
  ['message.pin', (vm, event) => vm.handle_socket_message_pinned(event)],
  ['message.unpin', (vm, event) => vm.handle_socket_message_pinned(event)],
  ['message.pin.updated', (vm, event) => vm.handle_socket_message_pinned(event)],
  ['message.delivered', (vm, event) => vm.handle_socket_message_delivered(event)],
  ['message.read', (vm, event) => vm.handle_socket_message_read(event)],
  ['typing.start', (vm, event) => vm.handle_socket_typing(event, true)],
  ['typing.stop', (vm, event) => vm.handle_socket_typing(event, false)],
  ['presence.update', (vm, event) => vm.handle_socket_presence_update(event)],
  ['group.member.joined', (vm, event) => vm.load_conversation_detail(event.conversation_id)],
  ['group.member.left', (vm, event) => vm.load_conversation_detail(event.conversation_id)],
  ['error', (vm, event) => console.error('Web socket server error', event)],
]);

const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮'];

export default {
  name: "OneChatView",
  components: { Sidebar, Navbar, FilePreviewComponent, EmojiPicker },

  data: () => ({
    csrfToken: '',

    // Conversations
    conversations: [],
    active_conversation_id: null,
    conversations_loading: true,
    conversations_loaded: false,

    // Messages
    messages: [],
    messages_loading: false,
    messages_offset: 0,
    messages_has_more: false,
    messages_total: 0,
    loading_more_messages: false,

    // Message input
    draft: "",
    sending_message: false,
    selected_file: null,
    selected_file_preview_url: null,

    // Reply / quote
    reply_to_message: null,

    // New conversation modal
    show_new_conversation_modal: false,
    new_conversation_mode: 'direct',
    new_conversation_user_id: "",
    new_group_name: "",
    new_group_user_ids: [],
    group_member_user_id: "",
    group_member_search_query: "",
    group_member_add_open: false,
    group_member_list_query: "",
    group_members_updating: false,
    new_conversation_message: "",
    starting_conversation: false,
    starting_group: false,
    available_users: [],
    users_loading: false,

    // Current user
    current_user_id: null,
    current_user: null,

    // Realtime WebSocket
    chat_socket: null,
    chat_socket_connected: false,
    chat_socket_last_error: null,
    typing_stop_timer: null,
    typing_user_ids_by_conversation: {},
    typing_clear_timers: {},

    // Search
    search_query: '',
    contact_search_query: '',
    conversation_filter: 'all',
    portal_syncing: false,
    _portal_sync_in_flight: false,
    portal_sync_error: '',

    // Attachment preview
    preview_modal: false,
    preview_file_name: "",
    preview_file_url: "",
    preview_file_access_level: null,
    selected_preview_file: null,
    editor_open_locks: {},

    // Edit
    editing_message_key: null,
    edit_draft: '',
    saving_edit: false,

    // Delete
    pending_delete_item: null,
    pending_delete_scope: 'me',
    deleting_message: false,
    pending_conversation_action: null,
    conversation_action_loading: false,
    hidden_conversation_ids: [],
    pending_remove_member: null,

    // Pin
    pinned_messages: [],
    show_pinned_panel: false,
    pinning_in_progress: false,

    // Toolbar hover (desktop)
    hovered_message_key: null,

    // Emoji picker
    quick_emojis: QUICK_EMOJIS,
    emoji_picker_key: null,
    reacting_message_id: null,

    // More menu
    active_conversation_menu_id: null,
    active_toolbar_key: null,
    toolbar_menu_direction: {},

    // Scroll control
    user_scrolled_up: false,
    new_messages_banner: false,

    // Info panel
    show_info_panel: false,
    info_tab: 'profile',
    shared_search_query: '',
    shared_filter: 'all',
    read_receipt_item: null,

    // Mobile
    mobile_action_item: null,

    // Long-press internals
    _long_press_timer: null,
    _touch_moved: false,
    _touch_start_x: 0,
    _touch_start_y: 0,

    // In-conversation message search
    show_message_search: false,
    message_search_query: '',
    message_search_index: 0,
    show_find_panel: false,
    search_filter_person: '',
    search_filter_date: '',
    conversation_participants: [],

    // BROWSER NOTIFICATION
    _messages_initialized: false,

    _is_adjusting_scroll: false,
    active_message_refresh_timer: null,
    active_message_refresh_in_flight: false,
  }),

  computed: {

    filtered_conversations() {
      let visible = this.conversations.filter(conv => this.is_visible_conversation(conv));
      if (this.conversation_filter !== 'all') {
        visible = visible.filter(conv => this.get_conversation_filter_key(conv) === this.conversation_filter);
      }
      if (!this.search_query) return this.sort_conversations(visible);
      const query = this.search_query.toLowerCase();
      return this.sort_conversations(visible.filter(conv => {
        const name = this.get_conversation_name(conv).toLowerCase();
        const last_message = conv.last_message?.content?.toLowerCase() || '';
        return name.includes(query) || last_message.includes(query);
      }));
    },

    conversation_filter_unread_counts() {
      return this.conversations.filter(conv => this.is_visible_conversation(conv)).reduce((counts, conv) => {
        const key = this.get_conversation_filter_key(conv);
        const unread = Number(conv.unread_count || 0);
        counts.all += unread;
        if (Reflect.has(counts, key)) Reflect.set(counts, key, Reflect.get(counts, key) + unread);
        return counts;
      }, { all: 0, chats: 0, spaces: 0, communities: 0 });
    },

    active_typing_names() {
      const ids = Reflect.get(this.typing_user_ids_by_conversation, this.active_conversation_id) || [];
      return ids
        .filter(user_id => String(user_id) !== String(this.current_user_id))
        .map(user_id => this.get_user_display_name_by_id(user_id))
        .filter(Boolean);
    },

    active_typing_text() {
      if (!this.active_typing_names.length) return '';
      if (this.active_typing_names.length === 1) {
        return `${this.active_typing_names[0]} is typing...`;
      }
      if (this.active_typing_names.length === 2) {
        return `${this.active_typing_names.join(' and ')} are typing...`;
      }
      return 'Several people are typing...';
    },

    rendered_stream() {
      const out = [];
      let last_date = null;
      this.messages.forEach((message, indx) => {
        if (message.kind === "unread") {
          out.push({ kind: "unread", _k: `unread-${indx}` });
          return;
        }
        const is_me = message.sender.id === this.current_user_id;
        const date_str = this.format_date(message.created_at);
        const prev_message = this.messages[indx - 1];
        const next_message = this.messages[indx + 1];
        const sender_key = String(message.sender?.id || '');
        const prev_sender_key = String(prev_message?.sender?.id || '');
        const next_sender_key = String(next_message?.sender?.id || '');
        const continues_previous =
          prev_message?.kind !== 'unread'
          && prev_sender_key === sender_key
          && this.format_date(prev_message?.created_at) === date_str;
        const continues_next =
          next_message?.kind !== 'unread'
          && next_sender_key === sender_key
          && this.format_date(next_message?.created_at) === date_str;
        if (date_str !== last_date) {
          out.push({ kind: "stamp", when: date_str, _k: `date-${indx}` });
          last_date = date_str;
        }
        const read_by = this.message_readers_excluding_current_user(message);
        out.push({
          kind: "msg",
          id: message.id,
          client_message_id: message.client_message_id,
          side: is_me ? "right" : "left",
          text: message.content,
          attachments: message.attachments || [],
          timestamp: this.format_time(message.created_at),
          edited_at: message.edited_at,
          is_pinned: message.is_pinned,
          pending: Boolean(message.pending),
          failed: Boolean(message.failed),
          reactions: message.reactions || [],
          read_by,
          read_count: read_by.length,
          delivered_count: message.delivered_count || 0,
          reply_to: message.reply_to || null,
          sender_name: this.get_sender_name(message.sender),
          show_sender: !continues_previous,
          show_sender_name: this.active_is_group && !continues_previous,
          show_avatar: !continues_previous,
          grouped_with_previous: continues_previous,
          grouped_with_next: continues_next,
          _k: `msg-${message.id}`,
        });
      });
      return out;
    },

    active_conversation() {
      return this.conversations.find(c => c.id === this.active_conversation_id);
    },

    filtered_users() {
      const users = [
        ...this.available_users
      ].filter(user => {
        if (this.new_conversation_mode !== 'group') return true;
        return String(user.id) !== String(this.current_user_id);
      }).sort((a, b) => {
        if (a.id === this.current_user_id) return -1;
        if (b.id === this.current_user_id) return 1;
        return 0;
      });
      if (!this.contact_search_query) return users;
      const query = this.contact_search_query.toLowerCase();
      return users.filter(user => {
        const full_name = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
        const email = (user.email || '').toLowerCase();
        const username = (user.username || '').toLowerCase();
        return full_name.includes(query) || email.includes(query) || username.includes(query);
      });
    },

    selected_group_users() {
      const selected = this.selected_group_user_set();
      return this.available_users.filter(user => selected.has(String(user.id)));
    },

    shared_files() {
      const files = [];
      for (const msg of this.messages) {
        if (msg.attachments && msg.attachments.length) {
          for (const f of msg.attachments) {
            files.push({
              ...f,
              created_at: msg.created_at,
              sender: msg.sender,
              message_id: msg.id,
              message_key: `msg-${msg.id}`,
            });
          }
        }
      }
      return files;
    },

    shared_files_count() {
      return this.shared_files.length;
    },

    shared_images() {
      return this.shared_files.filter(file => this.is_image_file(this.get_chat_file_name(file)));
    },

    shared_document_files() {
      return this.shared_files.filter(file => !this.is_image_file(this.get_chat_file_name(file)));
    },

    shared_links() {
      return this.get_shared_links();
    },

    shared_content_counts() {
      return ({
        all: this.shared_document_files.length + this.shared_images.length + this.shared_links.length,
        files: this.shared_document_files.length,
        images: this.shared_images.length,
        links: this.shared_links.length,
      });
    },

    filtered_shared_files() {
      if (!['all', 'files'].includes(this.shared_filter)) return [];
      return this.shared_document_files.filter(file => this.shared_item_matches_search([
        this.get_chat_file_name(file),
        this.get_sender_name(file.sender),
      ]));
    },

    filtered_shared_images() {
      if (!['all', 'images'].includes(this.shared_filter)) return [];
      return this.shared_images.filter(file => this.shared_item_matches_search([
        this.get_chat_file_name(file),
        this.get_sender_name(file.sender),
      ]));
    },

    filtered_shared_links() {
      if (!['all', 'links'].includes(this.shared_filter)) return [];
      return this.shared_links.filter(link => this.shared_item_matches_search([
        link.title,
        link.domain,
        link.url,
        this.get_sender_name(link.sender),
      ]));
    },

    has_filtered_shared_content() {
      return Boolean(
        this.filtered_shared_files.length
        || this.filtered_shared_images.length
        || this.filtered_shared_links.length
      );
    },

    direct_chat_user() {
      if (!this.active_conversation || this.active_is_group) return null;
      return this.normalize_chat_user(this.get_other_user_raw(this.active_conversation));
    },

    active_is_group() {
      return this.is_group_conversation(this.active_conversation);
    },

    active_conversation_members() {
      return this.get_conversation_members(this.active_conversation);
    },

    active_conversation_bio() {
      return this.get_conversation_bio(this.active_conversation);
    },

    active_group_can_manage() {
      if (!this.active_is_group) return false;
      const me = this.active_conversation_members.find(member => member.is_me);
      const role = String(me?.conversation_role || me?.group_role || '').toUpperCase();
      const created_by = this.active_conversation?.created_by;
      return role === 'ADMIN' || role === 'OWNER' || String(created_by) === String(this.current_user_id);
    },

    available_group_users() {
      if (!this.active_is_group) return [];
      const active_ids = new Set(this.active_conversation_members.map(member => String(member.id)));
      return this.available_users
        .filter(user => !active_ids.has(String(user.id)))
        .sort((a, b) => {
          const a_name = `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email || '';
          const b_name = `${b.first_name || ''} ${b.last_name || ''}`.trim() || b.email || '';
          return a_name.localeCompare(b_name);
        });
    },

    filtered_available_group_users() {
      const query = this.group_member_search_query.trim().toLowerCase();
      if (!query) return this.available_group_users;
      return this.available_group_users.filter(user => {
        const haystack = [
          user.first_name,
          user.last_name,
          user.full_name,
          user.name,
          user.email,
          user.username,
          user.role,
          user.department,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(query);
      });
    },

    filtered_active_conversation_members() {
      const query = this.group_member_list_query.trim().toLowerCase();
      if (!query) return this.active_conversation_members;
      return this.active_conversation_members.filter(member => {
        const haystack = [
          member.name,
          member.email,
          member.role,
          member.department,
          member.conversation_role,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(query);
      });
    },

    message_search_matches() {
      const q = this.message_search_query.trim().toLowerCase();
      const person_id = this.search_filter_person;
      const date_str = this.search_filter_date;

      if (!q && !person_id && !date_str) return [];

      return this.rendered_stream
        .filter(item => {
          if (item.kind !== 'msg') return false;
          if (q && !(item.text && item.text.toLowerCase().includes(q))) return false;

          if (person_id) {
            const msg = this.messages.find(m => m.id === item.id);
            if (!msg || String(msg.sender?.id) !== String(person_id)) return false;
          }

          if (date_str) {
            const msg = this.messages.find(m => m.id === item.id);
            if (!msg) return false;
            const msg_date = new Date(msg.created_at).toLocaleDateString('en-CA');
            if (msg_date !== date_str) return false;
          }

          return true;
        })
        .map(item => item._k);
    },

    today_date() {
      return new Date().toLocaleDateString('en-CA');
    },
  },

  mounted() {
    this.csrfToken = getCookie('csrftoken');
    this.load_initial_data();
    this.attach_modal_listener();
    document.addEventListener('click', this._close_popups_on_outside);
  },

  beforeUnmount() {
    this.stop_active_message_refresh();
    this.disconnect_chat_socket();
    this.detach_scroll_listener();
    this.revoke_preview_url();
    if (this.typing_stop_timer) clearTimeout(this.typing_stop_timer);
    Object.values(this.typing_clear_timers).forEach(timer => clearTimeout(timer));
    if (this._long_press_timer) clearTimeout(this._long_press_timer);
    document.removeEventListener('click', this._close_popups_on_outside);
    $('.modal-backdrop').remove();
    $('body').removeClass('modal-open').css('padding-right', '');
  },

  methods: {

    // ── BROWSER NOTIFICATION HELPER ───────────────────────────────────────
    fire_chat_browser_notification(sender_name, preview, conversation_id) {
      if (!('Notification' in window)) return;
      if (Notification.permission !== 'granted') return;

      const body = preview
        ? (preview.length > 80 ? preview.slice(0, 77) + '…' : preview)
        : '📎 Sent an attachment';

      const notif = new Notification(`${sender_name} — ONE Chat`, {
        body,
        tag: `aic-chat-conv-${conversation_id}`,
        requireInteraction: false,
        silent: false,
      });

      notif.onclick = () => {
        window.focus();
        if (this.$router) {
          this.$router.push('/chat');
        }
        notif.close();
      };

      setTimeout(() => notif.close(), 9000);
    },

    // ── SCROLL HELPERS ────────────────────────────────────────────────────

    _set_scroll_top(el, value) {
      this._is_adjusting_scroll = true;
      el.scrollTop = value;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this._is_adjusting_scroll = false;
        });
      });
    },

    scroll_to_bottom(force = false) {
      this.$nextTick(() => {
        const el = this.$refs.scroll_area;
        if (!el) return;
        if (force || !this.user_scrolled_up) {
          el.scrollTop = el.scrollHeight;
        }
      });
    },

    jump_to_bottom() {
      this.user_scrolled_up    = false;
      this.new_messages_banner = false;
      this.scroll_to_bottom(true);
    },

    attach_scroll_listener() {
      this.$nextTick(() => {
        const el = this.$refs.scroll_area;
        if (!el) return;
        el.removeEventListener('scroll', this._scroll_handler);
        this._scroll_handler = () => {
          if (this._is_adjusting_scroll) return;

          const area = this.$refs.scroll_area;
          if (!area) return;
          const distance = area.scrollHeight - area.scrollTop - area.clientHeight;
          this.user_scrolled_up = distance > 120;
          if (!this.user_scrolled_up) {
            this.new_messages_banner = false;
          }

          // Auto-load older messages when user scrolls near the very top
          if (area.scrollTop < 80 && this.messages_has_more && !this.loading_more_messages) {
            this.load_more_messages();
          }
        };
        el.addEventListener('scroll', this._scroll_handler, { passive: true });
      });
    },

    detach_scroll_listener() {
      const el = this.$refs.scroll_area;
      if (el && this._scroll_handler) {
        el.removeEventListener('scroll', this._scroll_handler);
      }
    },

    // ── Auth helpers ──────────────────────────────
    require_token_or_redirect() {
      const token = get_token();
      if (!token) {
        this.$router.push({ name: 'Login' });
        return null;
      }
      return token;
    },

    _auth_headers(token) {
      return ({
        'Authorization': `Token ${token}`,
        'X-Page-URL':    window.location.href,
        ...csrf_headers(this.csrfToken),
      });
    },

    connect_chat_socket() {
      if (this.chat_socket) return;
      const token = this.require_token_or_redirect();
      if (!token) return;

      this.chat_socket = new OneChatWebSocketClient({
        token,
        on_open: () => {
          this.chat_socket_connected = true;
          this.chat_socket_last_error = null;
          if (this.active_conversation_id) {
            this.chat_socket.subscribe(this.active_conversation_id);
          }
        },
        on_close: () => {
          this.chat_socket_connected = false;
        },
        on_error: (event) => {
          this.chat_socket_last_error = event;
        },
        on_event: (event) => this.handle_chat_socket_event(event),
      });

      this.chat_socket.connect();
    },

    disconnect_chat_socket() {
      if (!this.chat_socket) return;
      this.chat_socket.disconnect();
      this.chat_socket = null;
      this.chat_socket_connected = false;
    },

    is_chat_socket_open() {
      return Boolean(this.chat_socket && this.chat_socket.is_open());
    },

    wait_for_chat_socket(timeout_ms = 10000) {
      this.connect_chat_socket();
      if (this.is_chat_socket_open()) return Promise.resolve();

      return new Promise((resolve, reject) => {
        const started = Date.now();
        const timer = window.setInterval(() => {
          if (this.is_chat_socket_open()) {
            window.clearInterval(timer);
            resolve();
            return;
          }
          if (Date.now() - started > timeout_ms) {
            window.clearInterval(timer);
            reject(new Error('WebSocket is not connected'));
          }
        }, 50);
      });
    },

    async one_chat_request(type, payload = {}, timeout_ms = 30000) {
      await this.wait_for_chat_socket();
      return this.chat_socket.request(type, payload, timeout_ms);
    },

    start_active_message_refresh() {
      this.stop_active_message_refresh();
      this.active_message_refresh_timer = window.setInterval(() => {
        this.refresh_active_messages_silently();
      }, ACTIVE_MESSAGE_REFRESH_MS);
    },

    stop_active_message_refresh() {
      if (!this.active_message_refresh_timer) return;
      window.clearInterval(this.active_message_refresh_timer);
      this.active_message_refresh_timer = null;
    },

    async refresh_active_messages_silently() {
      const conversation_id = this.active_conversation_id;

      if (!this.can_refresh_active_messages_silently(conversation_id)) {
        return;
      }

      this.active_message_refresh_in_flight = true;

      try {
        const data = await this.fetch_active_messages_for_refresh(conversation_id);

        if (this.should_skip_silent_message_refresh(data, conversation_id)) {
          return;
        }

        this.apply_active_message_refresh_response(data, conversation_id);
        await this.refresh_pinned_messages_silently(conversation_id);
      } catch (error) {
        this.handle_silent_message_refresh_error(error);
      } finally {
        this.active_message_refresh_in_flight = false;
      }
    },

    can_refresh_active_messages_silently(conversation_id) {
      return Boolean(conversation_id)
        && !this.messages_loading
        && !this.loading_more_messages
        && !this.active_message_refresh_in_flight;
    },

    fetch_active_messages_for_refresh(conversation_id) {
      return this.one_chat_request('conversation.messages', {
        conversation_id,
        limit: 50,
        offset: 0,
      }, 15000);
    },

    should_skip_silent_message_refresh(data, conversation_id) {
      return String(conversation_id) !== String(this.active_conversation_id)
        || data?.status !== 'success';
    },

    apply_active_message_refresh_response(data, conversation_id) {
      const refresh_data = this.get_active_message_refresh_data(data);

      this.apply_silent_message_refresh(
        refresh_data.messages,
        refresh_data.has_more,
        refresh_data.total,
        conversation_id,
      );
    },

    get_active_message_refresh_data(data) {
      const response_data = Reflect.get(data || {}, 'data') || {};

      return ({
        messages: Reflect.get(response_data, 'messages') || [],
        has_more: Reflect.get(response_data, 'has_more'),
        total: Reflect.get(response_data, 'total'),
      });
    },

    handle_silent_message_refresh_error(error) {
      // Realtime events remain primary; silent refresh is only a fallback.
      console.warn('Failed to refresh active messages silently', error);
    },

    async refresh_pinned_messages_silently(conversation_id = this.active_conversation_id) {
      if (!conversation_id) return;
      const data = await this.one_chat_request('message.pinned.list', { conversation_id }, 15000);
      if (String(conversation_id) !== String(this.active_conversation_id)) return;
      if (data?.status === 'success') {
        this.pinned_messages = data.data || [];
      }
    },

    apply_silent_message_refresh(fetched = [], has_more, total, conversation_id) {
      this.update_silent_message_pagination(has_more, total);

      if (!fetched.length) return;

      const refresh_result = this.build_silent_message_refresh_result(fetched);

      if (!this.should_apply_silent_message_refresh(refresh_result)) {
        return;
      }

      this.commit_silent_message_refresh(refresh_result, conversation_id);
    },

    update_silent_message_pagination(has_more, total) {
      if (typeof has_more === 'boolean') {
        this.messages_has_more = has_more;
      }

      if (typeof total === 'number') {
        this.messages_total = total;
      }
    },

    build_silent_message_refresh_result(fetched) {
      const current_by_id = this.build_message_map(this.messages);
      const fetched_by_id = this.build_message_map(fetched);

      const patched_messages = this.messages.map(message => (
        this.patch_message_from_refresh(message, fetched_by_id)
      ));

      const changed = patched_messages.some((message, index) => (
        message !== Reflect.get(this.messages, index)
      ));

      const brand_new = fetched.filter(message => (
        !current_by_id.has(String(Reflect.get(message, 'id')))
      ));

      return ({
        patched_messages,
        brand_new,
        changed,
      });
    },

    build_message_map(messages = []) {
      return new Map(messages.map(message => [
        String(Reflect.get(message, 'id')),
        message,
      ]));
    },

    patch_message_from_refresh(message, fetched_by_id) {
      const server_message = fetched_by_id.get(String(Reflect.get(message, 'id')));

      if (!server_message || !this.messages_differ(message, server_message)) {
        return message;
      }

      return ({ ...message, ...server_message });
    },

    should_apply_silent_message_refresh({ changed, brand_new }) {
      return changed || brand_new.length > 0;
    },

    commit_silent_message_refresh(refresh_result, conversation_id) {
      const patched_messages = Reflect.get(refresh_result, 'patched_messages') || [];
      const brand_new = Reflect.get(refresh_result, 'brand_new') || [];
      const scroll_state = this.get_silent_refresh_scroll_state();
      const next_messages = brand_new.length
        ? this.sort_messages_by_time([...patched_messages, ...brand_new])
        : patched_messages;

      this.messages = this.remove_pending_messages_replaced_by_server(next_messages, brand_new);

      this.sync_pinned_messages_from_active_messages();
      this.handle_silent_refresh_new_messages(brand_new, conversation_id, scroll_state.was_at_bottom);
      this.restore_silent_refresh_scroll(scroll_state);
    },

    remove_pending_messages_replaced_by_server(messages, server_messages = []) {
      if (!server_messages.length) return messages;
      return messages.filter(message => {
        if (!message.pending) return true;
        const replaced = server_messages.some(server_message => this.pending_message_matches_server(message, server_message));
        if (replaced) {
          (message.attachments || []).forEach(file => {
            if (file.file_url?.startsWith?.('blob:')) URL.revokeObjectURL(file.file_url);
          });
        }
        return !replaced;
      });
    },

    pending_message_matches_server(pending_message, server_message) {
      if (this.pending_message_has_server_client_id(pending_message, server_message)) {
        return true;
      }

      if (!this.is_server_message_from_current_user(server_message)) {
        return false;
      }

      return this.pending_message_content_matches(pending_message, server_message)
        && this.pending_message_attachments_match(pending_message, server_message);
    },

    pending_message_has_server_client_id(pending_message, server_message) {
      const client_id = this.get_server_message_client_id(server_message);

      return Boolean(client_id)
        && String(pending_message.client_message_id) === String(client_id);
    },

    get_server_message_client_id(server_message) {
      return server_message.client_message_id
        || server_message.local_id
        || server_message.temp_id;
    },

    is_server_message_from_current_user(server_message) {
      return String(server_message.sender?.id) === String(this.current_user_id);
    },

    pending_message_content_matches(pending_message, server_message) {
      return (pending_message.content || '') === (server_message.content || '');
    },

    pending_message_attachments_match(pending_message, server_message) {
      const pending_names = this.get_message_attachment_names(pending_message);

      if (!pending_names.length) {
        return true;
      }

      const server_names = this.get_message_attachment_names(server_message);

      return pending_names.every(name => server_names.includes(name));
    },

    get_message_attachment_names(message) {
      return (message.attachments || []).map(file => this.get_chat_file_name(file));
    },

    get_silent_refresh_scroll_state() {
      const el = this.$refs.scroll_area;

      return ({
        was_at_bottom: !this.user_scrolled_up,
        previous_scroll_top: el ? el.scrollTop : 0,
      });
    },

    handle_silent_refresh_new_messages(brand_new, conversation_id, was_at_bottom) {
      if (!brand_new.length) return;

      this.messages_offset += brand_new.length;
      this.handle_silent_new_messages(brand_new, conversation_id, was_at_bottom);
    },

    restore_silent_refresh_scroll({ was_at_bottom, previous_scroll_top }) {
      this.$nextTick(() => {
        const scroll_el = this.$refs.scroll_area;

        if (!scroll_el) return;

        if (was_at_bottom) {
          scroll_el.scrollTop = scroll_el.scrollHeight;
          return;
        }

        this._set_scroll_top(scroll_el, previous_scroll_top);
      });
    },

    messages_differ(local_message, server_message) {
      const primitive_keys = [
        'content',
        'edited_at',
        'is_pinned',
        'delivered_count',
        'read_count',
      ];

      const has_primitive_difference = primitive_keys.some(key => (
        Reflect.get(local_message || {}, key) !== Reflect.get(server_message || {}, key)
      ));

      if (has_primitive_difference) {
        return true;
      }

      const json_fields = [
        ['attachments', []],
        ['reactions', []],
        ['read_by', []],
        ['reply_to', null],
      ];

      return json_fields.some(([key, fallback]) => (
        JSON.stringify(Reflect.get(local_message || {}, key) || fallback)
          !== JSON.stringify(Reflect.get(server_message || {}, key) || fallback)
      ));
    },

    sort_messages_by_time(messages) {
      return [...messages].sort((a, b) => {
        const a_time = new Date(a.created_at || 0).getTime();
        const b_time = new Date(b.created_at || 0).getTime();
        if (a_time !== b_time) return a_time - b_time;
        return Number(a.id || 0) - Number(b.id || 0);
      });
    },

    handle_silent_new_messages(messages, conversation_id, was_at_bottom) {
      const incoming = messages.filter(message => String(message.sender?.id) !== String(this.current_user_id));
      if (!incoming.length) return;
      if (was_at_bottom) {
        this.mark_as_read(conversation_id);
      } else {
        this.new_messages_banner = true;
      }
    },

    sync_pinned_messages_from_active_messages() {
      this.messages.forEach(message => {
        if (message?.is_pinned) {
          this.sync_pinned_message_from_update(message);
          return;
        }
        this.remove_deleted_message_from_pinned(message.id);
      });
    },

    handle_chat_socket_event(event) {
      if (IGNORED_CHAT_SOCKET_EVENTS.has(event.type)) return;
      const handler = CHAT_SOCKET_EVENT_HANDLERS.get(event.type);
      if (handler) handler(this, event);
    },

    handle_socket_conversation_created(event) {
      const conversation = event.conversation;
      if (!conversation?.id) return;
      this.upsert_conversation(conversation);
      this.refresh_unread_chats();
    },

    handle_socket_message_created(message) {
      if (!message?.id) return;
      const conversation_id = message.conversation || message.conversation_id;
      const is_active = this.is_active_conversation_id(conversation_id);
      this.upsert_conversation_from_message(message, { is_active });

      if (!is_active) {
        this.refresh_unread_chats();
        return;
      }

      const was_at_bottom = this.upsert_socket_message(message);
      this.handle_incoming_socket_message(message, conversation_id);
      this.scroll_after_socket_message(message, conversation_id, was_at_bottom);
    },

    is_active_conversation_id(conversation_id) {
      return String(conversation_id) === String(this.active_conversation_id);
    },

    refresh_unread_chats() {
      window.dispatchEvent(new Event('refresh-unread-chats'));
    },

    upsert_socket_message(message) {
      const was_at_bottom = !this.user_scrolled_up;
      const existing_message = this.messages.find(m => m.id === message.id);
      if (!existing_message) {
        this.messages.push(message);
        this.messages_offset += 1;
        this.remove_pending_socket_message(message);
        return was_at_bottom;
      }
      this.messages.splice(this.messages.indexOf(existing_message), 1, {
        ...existing_message,
        ...message,
        pending: false,
        failed: false,
      });
      return was_at_bottom;
    },

    remove_pending_socket_message(message) {
      const before = this.messages.length;
      this.messages
        .filter(item => item.pending && this.pending_message_matches_server(item, message))
        .flatMap(item => item.attachments || [])
        .forEach(file => {
          if (file.file_url?.startsWith?.('blob:')) URL.revokeObjectURL(file.file_url);
        });
      this.messages = this.messages.filter(item => {
        if (!item.pending || String(item.id) === String(message.id)) return true;
        return !this.pending_message_matches_server(item, message);
      });
      if (this.messages.length !== before) {
        this.messages_offset = Math.max(0, this.messages_offset - (before - this.messages.length));
      }
    },

    handle_incoming_socket_message(message, conversation_id) {
      if (message.sender?.id !== this.current_user_id) {
        this.fire_chat_browser_notification(
          this.get_sender_name(message.sender),
          message.content || (message.attachments?.length ? 'Attachment' : ''),
          conversation_id
        );
        if (this.is_chat_socket_open()) {
          this.chat_socket.mark_delivered(conversation_id, [message.id]);
        }
      }
    },

    scroll_after_socket_message(message, conversation_id, was_at_bottom) {
      this.$nextTick(() => {
        if (was_at_bottom || message.sender?.id === this.current_user_id) {
          this.user_scrolled_up = false;
          this.new_messages_banner = false;
          this.scroll_to_bottom(true);
          if (message.sender?.id !== this.current_user_id) {
            this.mark_as_read(conversation_id);
          }
        } else {
          this.new_messages_banner = true;
        }
      });
    },

    handle_socket_message_deleted(event) {
      const delete_data = this.get_deleted_message_data(event);

      if (!delete_data.message_id) return;

      this.remove_deleted_message_from_active_conversation(delete_data);
      this.reload_conversations_if_last_message_deleted(delete_data);
      this.refresh_unread_chats();
    },

    get_deleted_message_data(event) {
      const data = event.message || event;

      return ({
        message_id: data.message_id || data.id,
        conversation_id: data.conversation_id || event.conversation_id || data.conversation,
      });
    },

    remove_deleted_message_from_active_conversation({ message_id, conversation_id }) {
      if (!this.is_active_conversation_id(conversation_id)) return;

      const before_count = this.messages.length;

      this.messages = this.messages.filter(
        message => String(message.id) !== String(message_id)
      );

      if (this.messages.length !== before_count) {
        this.messages_offset = Math.max(0, this.messages_offset - 1);
      }

      this.remove_deleted_message_from_pinned(message_id);
    },

    remove_deleted_message_from_pinned(message_id) {
      this.pinned_messages = this.pinned_messages.filter(
        message => String(message.id) !== String(message_id)
      );
    },

    reload_conversations_if_last_message_deleted({ message_id, conversation_id }) {
      const conversation = this.conversations.find(
        item => String(item.id) === String(conversation_id)
      );

      if (!this.is_deleted_message_last_message(conversation, message_id)) return;

      this.load_conversations(true);
    },

    is_deleted_message_last_message(conversation, message_id) {
      return String(conversation?.last_message?.id) === String(message_id);
    },

    get_socket_message_payload(event) {
      return (
        event.message
        || event.updated_message
        || event.message_data
        || event.data?.message
        || event.data?.updated_message
        || event.data?.message_data
        || event.data
        || event
      );
    },

    normalize_socket_message_patch(event) {
      const message = this.get_socket_message_payload(event) || {};
      const message_id = this.get_socket_message_id(event);
      if (!message_id) return null;
      return ({
        ...event.data,
        ...event,
        ...message,
        id: message.id || message.message_id || message_id,
      });
    },

    get_socket_message_id(event) {
      const message = this.get_socket_message_payload(event);
      return (
        message?.message_id
        || message?.id
        || message?.pk
        || event.data?.message_id
        || event.data?.id
        || event.message_id
        || event.id
      );
    },

    get_socket_conversation_id(event = {}) {
      const message = this.get_socket_message_payload(event) || {};
      const event_data = Reflect.get(event, 'data') || {};

      return this.first_present_value([
        Reflect.get(message, 'conversation_id'),
        this.resolve_conversation_reference(Reflect.get(message, 'conversation')),
        Reflect.get(event_data, 'conversation_id'),
        this.resolve_conversation_reference(Reflect.get(event_data, 'conversation')),
        Reflect.get(event, 'conversation_id'),
        this.resolve_conversation_reference(Reflect.get(event, 'conversation')),
      ]);
    },

    resolve_conversation_reference(conversation) {
      if (!conversation) {
        return null;
      }

      if (typeof conversation === 'object') {
        return Reflect.get(conversation, 'id');
      }

      return conversation;
    },

    first_present_value(values = []) {
      return values.find(value => value !== undefined && value !== null && value !== '');
    },

    normalize_id_set(ids = []) {
      return new Set(ids.map(id => String(id)));
    },

    read_receipt_user_id(reader) {
      return reader?.user?.id || reader?.id || reader?.user_id;
    },

    normalize_read_receipt_reader(reader) {
      const user = reader?.user || reader || {};
      return ({
        ...user,
        read_at: reader?.read_at || user.read_at,
      });
    },

    message_readers_excluding_current_user(message) {
      const seen = new Set();
      return (message?.read_by || []).reduce((readers, reader) => {
        const user_id = this.read_receipt_user_id(reader);
        if (!user_id || String(user_id) === String(this.current_user_id)) return readers;
        if (seen.has(String(user_id))) return readers;
        seen.add(String(user_id));
        readers.push(this.normalize_read_receipt_reader(reader));
        return readers;
      }, []);
    },

    patch_message_metadata(message_id, patch = {}) {
      if (!message_id) return null;
      const idx = this.messages.findIndex(item => String(item.id) === String(message_id));
      if (idx === -1) return null;

      const next_message = {
        ...Reflect.get(this.messages, idx),
        ...patch,
      };
      this.messages.splice(idx, 1, next_message);
      this.messages = [
        ...this.messages
      ];
      this.patch_pinned_message_metadata(message_id, patch);
      this.patch_conversation_last_message(next_message);
      return next_message;
    },

    patch_pinned_message_metadata(message_id, patch = {}) {
      const idx = this.pinned_messages.findIndex(item => String(item.id) === String(message_id));
      if (idx === -1) return;
      this.pinned_messages.splice(idx, 1, {
        ...Reflect.get(this.pinned_messages, idx),
        ...patch,
      });
      this.pinned_messages = [...this.pinned_messages];
    },

    patch_conversation_last_message(message) {
      const conversation_id = message?.conversation_id
        || (typeof message?.conversation === 'object' ? message.conversation.id : message?.conversation);
      if (!conversation_id) return;
      const idx = this.conversations.findIndex(item => String(item.id) === String(conversation_id));
      if (idx === -1) return;
      const conversation = Reflect.get(this.conversations, idx);
      if (String(conversation?.last_message?.id) !== String(message.id)) return;
      this.conversations.splice(idx, 1, {
        ...conversation,
        last_message: {
          ...conversation.last_message,
          ...message,
        },
      });
      this.conversations = [...this.conversations];
    },

    handle_socket_message_reacted(event) {
      const message = this.normalize_socket_message_patch(event);
      if (!message?.id) return;
      const conversation_id = this.get_socket_conversation_id(event);
      if (conversation_id && !this.is_active_conversation_id(conversation_id)) return;
      this.patch_message_metadata(message.id, {
        ...message,
        reactions: message.reactions || [],
      });
    },

    handle_socket_message_updated(event) {
      const message = this.normalize_socket_message_patch(event);
      if (!message?.id) return;
      const conversation_id = this.get_socket_conversation_id(event);
      if (conversation_id && !this.is_active_conversation_id(conversation_id)) return;
      const updated = this.patch_message_metadata(message.id, message);
      if (updated?.is_pinned) {
        this.sync_pinned_message_from_update(updated);
      } else if (Object.prototype.hasOwnProperty.call(message, 'is_pinned')) {
        this.remove_deleted_message_from_pinned(message.id);
      }
    },

    handle_socket_message_pinned(event) {
      const message = this.normalize_socket_message_patch(event);
      if (!message?.id) return;

      const pin_value = this.get_socket_pin_value(event, message);
      const conversation_id = this.get_socket_conversation_id(event);
      if (conversation_id && !this.is_active_conversation_id(conversation_id)) return;

      const updated = this.patch_message_metadata(message.id, {
        ...message,
        is_pinned: pin_value,
      });

      if (pin_value) {
        this.sync_pinned_message_from_update(updated || {
          ...message, is_pinned: true });
      } else {
        this.remove_deleted_message_from_pinned(message.id);
      }

      this.refresh_pinned_messages_silently(conversation_id)
        .catch(this.handle_silent_pinned_refresh_error);
    },

    get_socket_pin_value(event, message) {
      const pin_value = this.get_first_existing_value([
        [message, 'is_pinned'],
        [message, 'pin'],
        [event.data || {}, 'is_pinned'],
        [event.data || {}, 'pin'],
        [event, 'is_pinned'],
        [event, 'pin'],
      ]);

      if (pin_value !== undefined) {
        return Boolean(pin_value);
      }

      return event.type !== 'message.unpinned';
    },

    sync_pinned_message_from_update(message) {
      if (!message?.id) return;
      const idx = this.pinned_messages.findIndex(item => String(item.id) === String(message.id));
      if (idx === -1) {
        this.pinned_messages = [...this.pinned_messages, message];
        return;
      }
      this.pinned_messages.splice(idx, 1, {
        ...Reflect.get(this.pinned_messages, idx),
        ...message,
      });
      this.pinned_messages = [...this.pinned_messages];
    },

    handle_socket_message_delivered(event) {
      if (String(event.conversation_id) !== String(this.active_conversation_id)) return;
      const ids = this.normalize_id_set(event.message_ids || []);
      this.messages = this.messages.map(message => {
        if (!ids.has(String(message.id))) return message;
        return ({
          ...message,
          delivered_count: Math.max(Number(message.delivered_count || 0), 1),
        });
      });
    },

    handle_socket_message_read(event) {
      const conv = this.conversations.find(c => String(c.id) === String(event.conversation_id));
      if (conv && String(event.user_id) === String(this.current_user_id)) {
        conv.unread_count = 0;
      }
      if (String(event.conversation_id) !== String(this.active_conversation_id)) return;
      this.messages = this.messages.map(message => {
        if (!this.socket_read_applies_to_message(message, event)) return message;
        const read_by = this.merge_socket_read_by(message.read_by || [], event);
        const visible_read_by = this.message_readers_excluding_current_user({ read_by });
        return ({
          ...message,
          read_by,
          read_count: visible_read_by.length,
        });
      });
      window.dispatchEvent(new Event('refresh-unread-chats'));
    },

    socket_read_applies_to_message(message, event) {
      const ids = this.normalize_id_set(event.message_ids || []);
      if (ids.size) return ids.has(String(message.id));
      if (!event.last_message_id) return false;
      const message_number = Number(message.id);
      const last_message_number = Number(event.last_message_id);
      if (Number.isFinite(message_number) && Number.isFinite(last_message_number)) {
        return message_number <= last_message_number;
      }
      return String(message.id) === String(event.last_message_id);
    },

    merge_socket_read_by(read_by, event) {
      const user_id = this.get_socket_reader_id(event);

      if (this.should_skip_socket_reader(read_by, user_id)) {
        return read_by;
      }

      return [
        ...read_by,
        this.get_socket_reader(event, user_id),
      ];
    },

    get_socket_reader_id(event) {
      return event.user_id || event.reader?.id || event.user?.id;
    },

    should_skip_socket_reader(read_by, user_id) {
      return !user_id
        || String(user_id) === String(this.current_user_id)
        || this.socket_reader_exists(read_by, user_id);
    },

    socket_reader_exists(read_by, user_id) {
      return read_by.some(reader => (
        String(this.read_receipt_user_id(reader)) === String(user_id)
      ));
    },

    get_socket_reader(event, user_id) {
      return event.reader
        || event.user
        || this.find_available_user_by_id(user_id)
        || { id: user_id };
    },

    find_available_user_by_id(user_id) {
      return this.available_users.find(user => (
        String(user.id) === String(user_id)
      ));
    },

    handle_socket_typing(event, is_typing) {
      const conversation_id = event.conversation_id;
      const user_id = event.user_id;
      if (!conversation_id || !user_id || String(user_id) === String(this.current_user_id)) return;

      const current_ids = Reflect.get(this.typing_user_ids_by_conversation, conversation_id) || [];
      const next_ids = is_typing
        ? [...new Set([...current_ids, user_id])]
        : current_ids.filter(id => String(id) !== String(user_id));

      Reflect.set(this.typing_user_ids_by_conversation, conversation_id, next_ids);
      this.typing_user_ids_by_conversation = { ...this.typing_user_ids_by_conversation };

      const timer_key = `${conversation_id}:${user_id}`;
      const existing_timer = Reflect.get(this.typing_clear_timers, timer_key);
      if (existing_timer) {
        clearTimeout(existing_timer);
      }
      if (is_typing) {
        Reflect.set(this.typing_clear_timers, timer_key, setTimeout(() => {
          this.handle_socket_typing({ conversation_id, user_id }, false);
        }, 2500));
      } else {
        Reflect.deleteProperty(this.typing_clear_timers, timer_key);
      }
    },

    handle_socket_presence_update(event) {
      const update_user = user => {
        if (!user || String(user.id) !== String(event.user_id)) return user;
        return ({
          ...user,
          is_online: event.status === 'online',
          presence: event.status,
          last_seen: event.last_seen,
        });
      };

      this.available_users = this.available_users.map(update_user);
      this.conversations = this.conversations.map(conversation => ({
        ...conversation,
        participants: (conversation.participants || []).map(participant => {
          return participant.user
            ? { ...participant, user: update_user(participant.user) }
            : update_user(participant);
        }),
      }));
    },

    upsert_conversation_from_message(message, { is_active = false } = {}) {
      const conversation_id = message.conversation || message.conversation_id;
      const existing = this.conversations.find(c => String(c.id) === String(conversation_id));
      if (!existing) {
        this.load_conversations(true);
        return;
      }
      const is_incoming = String(message.sender?.id) !== String(this.current_user_id);
      const unread_count = is_incoming && !is_active
        ? Number(existing.unread_count || 0) + 1
        : Number(existing.unread_count || 0);
      this.upsert_conversation({
        ...existing,
        last_message: message,
        unread_count,
        updated_at: message.created_at || existing.updated_at,
        cleared_for_me: false,
        hidden_for_me: false,
      });
    },

    send_typing_start() {
      if (!this.active_conversation_id || !this.is_chat_socket_open()) return;
      this.chat_socket.start_typing(this.active_conversation_id);
      if (this.typing_stop_timer) clearTimeout(this.typing_stop_timer);
      this.typing_stop_timer = setTimeout(() => {
        if (this.is_chat_socket_open()) {
          this.chat_socket.stop_typing(this.active_conversation_id);
        }
      }, 1600);
    },

    attach_modal_listener() {
      this.$nextTick(() => {
        const $modal = $('#newConversationModal');
        $modal.off('hidden.bs.modal shown.bs.modal show.bs.modal');
        $modal.on('show.bs.modal', () => {
          $('.modal-backdrop').remove();
          $('body').removeClass('modal-open');
          $('body').css('padding-right', '');
          $('body').css('overflow', '');
        });
        $modal.on('hidden.bs.modal', () => {
          this.new_conversation_mode = 'direct';
          this.new_conversation_user_id = '';
          this.new_group_name = '';
          this.new_group_user_ids = [];
          this.new_conversation_message = '';
          this.contact_search_query = '';
          this.starting_conversation = false;
          this.starting_group = false;
          $('.modal-backdrop').remove();
          $('body').removeClass('modal-open');
          $('body').css('padding-right', '');
          $('body').css('overflow', '');
        });
      });
    },

    get_sender_name(sender) {
      if (!sender) return 'Unknown';
      const full_name = `${sender.first_name || ''} ${sender.last_name || ''}`.trim();
      return full_name || sender.username || 'Unknown';
    },

    get_user_display_name_by_id(user_id) {
      const user = this.available_users.find(item => String(item.id) === String(user_id));
      if (user) return this.get_sender_name(user);
      const participant = this.active_conversation?.participants?.find(item => {
        const participant_user = item.user || item;
        return String(participant_user.id) === String(user_id);
      });
      return this.get_sender_name(participant?.user || participant);
    },

    get_other_user(conversation) {
      const participants = conversation?.participants || [];
      const other_participant = participants.find(
        p => (p.user || p).id !== this.current_user_id) || participants[0];
      const user = other_participant?.user || other_participant;
      const is_self = user?.id === this.current_user_id;
      const raw_name = user
        ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username
        : 'Unknown';
      const name = is_self ? `${raw_name} (Me)` : raw_name;
      const avatar = user ? user.avatar || `https://i.pravatar.cc/40?u=${user.id}` : null;
      return ({ name, avatar });
    },

    first_chat_value(values, fallback = '') {
      const value = values.find((entry) => entry);
      if (value) return value;
      return fallback;
    },

    conversation_type_value(conversation) {
      return this.first_chat_value([
        conversation?.conversation_type,
        conversation?.type,
        conversation?.chat_type,
        conversation?.conversation_type_name,
        conversation?.group?.type,
      ]);
    },

    normalized_conversation_type(conversation) {
      return String(this.conversation_type_value(conversation)).toLowerCase();
    },

    has_group_identity(conversation) {
      return Boolean(conversation?.is_group || conversation?.group_id || conversation?.group);
    },

    type_matches_group_conversation(type) {
      return [
        'group',
        'workspace',
        'space',
        'community',
      ].some((keyword) => type.includes(keyword));
    },

    is_group_conversation(conversation) {
      const type = this.normalized_conversation_type(conversation);
      return this.has_group_identity(conversation) || this.type_matches_group_conversation(type);
    },

    is_community_conversation(type) {
      return type.includes('community');
    },

    is_space_conversation(type) {
      return type.includes('space');
    },

    is_workspace_conversation(conversation, type) {
      return type.includes('workspace') || Boolean(conversation?.workspace_id || conversation?.workspace);
    },

    get_conversation_kind_label(conversation) {
      if (!conversation) return '';
      const type = this.normalized_conversation_type(conversation);
      if (this.is_community_conversation(type)) return 'Community';
      if (this.is_space_conversation(type)) return 'Space';
      if (this.is_workspace_conversation(conversation, type)) return 'Workspace';
      if (this.is_group_conversation(conversation)) return 'Group chat';
      return 'Team member';
    },

    get_conversation_filter_key(conversation) {
      const label = this.get_conversation_kind_label(conversation).toLowerCase();
      if (label === 'space') return 'spaces';
      if (label === 'community') return 'communities';
      return this.is_group_conversation(conversation) ? 'spaces' : 'chats';
    },

    is_visible_conversation(conversation) {
      const has_messages = conversation?.last_message != null;
      if ((conversation?.hidden_for_me || conversation?.is_hidden_for_me) && !has_messages) return false;
      const is_currently_active = conversation?.id === this.active_conversation_id;
      const is_group = this.is_group_conversation(conversation);
      const is_cleared_for_me = conversation?.cleared_for_me || conversation?.is_cleared_for_me;
      return has_messages || is_currently_active || is_group || is_cleared_for_me;
    },

    get_conversation_name(conversation) {
      if (!conversation) return 'Unknown';
      if (this.is_group_conversation(conversation)) {
        return (
          conversation.name
          || conversation.title
          || conversation.group_name
          || conversation.group?.name
          || 'Group'
        );
      }
      return this.get_other_user(conversation).name;
    },

    get_conversation_subtitle(conversation) {
      if (!conversation) return '';
      const last_message = conversation.last_message;
      if (!last_message && (conversation.cleared_for_me || conversation.is_cleared_for_me)) return 'No messages yet';
      const snippet = last_message?.content || (last_message?.attachments?.length ? 'Attachment' : 'No messages yet');
      if (!last_message || snippet === 'No messages yet') return snippet;
      const sender = this.get_sender_name(last_message.sender);
      const prefix = this.is_group_conversation(conversation) && sender !== 'Unknown'
        ? `${this.get_initials(sender)}: `
        : '';
      return this.truncate_text(`${prefix}${snippet}`, 52);
    },

    get_participant_user(raw_participant) {
      return raw_participant?.user || raw_participant || {};
    },

    get_chat_user_id(raw_participant, participant) {
      return this.first_chat_value([participant.id, raw_participant?.user_id, raw_participant?.id], null);
    },

    get_known_chat_user(user_id) {
      return this.available_users.find(u => String(u.id) === String(user_id)) || {};
    },

    merge_chat_user_data(known_user, participant) {
      return ({ ...known_user, ...participant });
    },

    chat_user_full_name(user) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    },

    chat_user_name(user) {
      return this.first_chat_value([
        this.chat_user_full_name(user),
        user.full_name,
        user.name,
        user.username,
        user.email,
      ], 'Unknown');
    },

    chat_user_email(user, known_user) {
      return this.first_chat_value([user.email, known_user.email]);
    },

    chat_user_role(user, known_user) {
      return this.first_chat_value([user.role, user.job_title, user.position, user.title, known_user.role]);
    },

    chat_user_department(user, known_user) {
      return this.first_chat_value([user.department, user.team, user.team_name, known_user.department, known_user.team]);
    },

    chat_user_bio(user, known_user) {
      return this.first_chat_value([user.bio, user.about, user.profile_bio, user.description, known_user.bio, known_user.about]);
    },

    chat_user_conversation_role(raw_participant, user) {
      return this.first_chat_value([raw_participant?.role_name, raw_participant?.conversation_role_name, user.conversation_role]);
    },

    chat_user_group_role(raw_participant, user) {
      return this.first_chat_value([raw_participant?.group_role, user.group_role]);
    },

    chat_user_left_at(raw_participant, user) {
      return this.first_chat_value([raw_participant?.left_at, user.left_at], null);
    },

    normalize_chat_user(raw_participant) {
      const participant = this.get_participant_user(raw_participant);
      const user_id = this.get_chat_user_id(raw_participant, participant);
      const known_user = this.available_users.find(u => String(u.id) === String(user_id)) || {};
      const user = this.merge_chat_user_data(known_user, participant);
      const name = this.chat_user_name(user);

      return ({
        ...user,
        id: user_id,
        name,
        initials: this.get_initials(name),
        email: this.chat_user_email(user, known_user),
        role: this.chat_user_role(user, known_user),
        department: this.chat_user_department(user, known_user),
        bio: this.chat_user_bio(user, known_user),
        conversation_role: this.chat_user_conversation_role(raw_participant, user),
        group_role: this.chat_user_group_role(raw_participant, user),
        left_at: this.chat_user_left_at(raw_participant, user),
        is_me: String(user_id) === String(this.current_user_id),
      });
    },

    get_raw_conversation_members(conversation) {
      return this.first_chat_value([
        conversation?.participants,
        conversation?.members,
        conversation?.group?.members,
      ], []);
    },

    get_message_sender_members() {
      return this.messages
        .filter(message => message.sender)
        .map(message => message.sender);
    },

    get_conversation_member_source(conversation) {
      const raw_members = this.get_raw_conversation_members(conversation);
      if (raw_members.length) return raw_members;
      return this.is_group_conversation(conversation) ? this.get_message_sender_members() : [];
    },

    unique_active_members(raw_members) {
      const seen = new Set();
      return raw_members
        .map(p => this.normalize_chat_user(p))
        .filter(member => {
          if (member.left_at) return false;
          const key = String(member.id || member.email || member.name);
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
    },

    fallback_direct_members(conversation, members) {
      if (members.length || this.is_group_conversation(conversation)) return members;
      return [this.normalize_chat_user(this.get_other_user_raw(conversation))];
    },

    get_conversation_members(conversation) {
      if (!conversation) return [];
      const raw_members = this.get_conversation_member_source(conversation);
      const members = this.unique_active_members(raw_members);
      return this.fallback_direct_members(conversation, members);
    },

    group_conversation_bio(conversation) {
      return this.first_chat_value([
        conversation.description,
        conversation.bio,
        conversation.about,
        conversation.topic,
        conversation.group?.description,
        conversation.group?.bio,
      ]);
    },

    direct_conversation_bio(conversation) {
      const other_user = this.normalize_chat_user(this.get_other_user_raw(conversation));
      return this.first_chat_value([other_user.bio, other_user.role, other_user.department]);
    },

    get_other_user_raw(conversation) {
      const participants = conversation?.participants || [];
      const other_participant = participants.find(
        p => String((p.user || p).id) !== String(this.current_user_id)
      ) || participants[0];
      return other_participant?.user || other_participant || null;
    },

    get_conversation_bio(conversation) {
      if (!conversation) return '';
      if (this.is_group_conversation(conversation)) {
        return this.group_conversation_bio(conversation);
      }
      return this.direct_conversation_bio(conversation);
    },

    get_conversation_creator_name(conversation) {
      const creator_name = this.first_chat_value([
        conversation?.created_by_name,
        conversation?.creator_name,
        conversation?.owner_name,
        conversation?.group?.created_by_name,
        conversation?.group?.creator_name,
      ]);
      if (creator_name) return creator_name;
      const creator = this.first_chat_value([
        conversation?.created_by_user,
        conversation?.created_by,
        conversation?.creator,
        conversation?.owner,
        conversation?.group?.created_by_user,
        conversation?.group?.created_by,
      ], null);
      if (!creator) return '';
      if (typeof creator === 'object') {
        return this.chat_user_name(creator);
      }
      const known_user = this.get_known_chat_user(creator);
      return known_user?.id ? this.chat_user_name(known_user) : '';
    },

    selected_group_user_set() {
      return new Set(this.new_group_user_ids.map(id => String(id)));
    },

    is_group_user_selected(user_id) {
      return this.selected_group_user_set().has(String(user_id));
    },

    toggle_group_user(user_id) {
      const selected = this.selected_group_user_set();
      const key = String(user_id);
      if (selected.has(key)) {
        this.new_group_user_ids = this.new_group_user_ids.filter(id => String(id) !== key);
      } else {
        this.new_group_user_ids = [...this.new_group_user_ids, user_id];
      }
    },

    select_modal_user(user) {
      if (this.new_conversation_mode === 'group') {
        this.toggle_group_user(user.id);
        return;
      }
      this.new_conversation_user_id = user.id;
    },

    close_new_conversation_modal() {
      const $modal = $('#newConversationModal');
      $modal.removeClass('show').css('display', 'none').attr('aria-hidden', 'true');
      $modal.removeAttr('aria-modal').removeAttr('role');
      $('.modal-backdrop').remove();
      $('body').removeClass('modal-open').css('padding-right', '').css('overflow', '');
      this.new_conversation_mode = 'direct';
      this.new_conversation_user_id = '';
      this.new_group_name = '';
      this.new_group_user_ids = [];
      this.new_conversation_message = '';
      this.contact_search_query = '';
      $modal.removeData('bs.modal');
    },

    upsert_conversation(conversation) {
      if (!conversation?.id) return;
      const is_locally_hidden = this.hidden_conversation_ids.includes(conversation.id);
      if ((is_locally_hidden || conversation.hidden_for_me || conversation.is_hidden_for_me) && !conversation.last_message) {
        this.conversations = this.conversations.filter(c => c.id !== conversation.id);
        if (this.active_conversation_id === conversation.id) {
          this.active_conversation_id = null;
          this.messages = [];
          this.pinned_messages = [];
          this.show_info_panel = false;
        }
        return;
      }
      if (is_locally_hidden && conversation.last_message) {
        this.hidden_conversation_ids = this.hidden_conversation_ids.filter(id => id !== conversation.id);
      }
      const existing_index = this.conversations.findIndex(c => c.id === conversation.id);
      if (existing_index !== -1) {
        const existing = Reflect.get(this.conversations, existing_index);
        this.conversations.splice(existing_index, 1, {
          ...existing,
          ...conversation,
          participants: conversation.participants?.length
            ? conversation.participants
            : existing.participants,
          member_count: conversation.member_count || existing.member_count,
        });
      } else {
        this.conversations.unshift(conversation);
      }
    },

    format_time(datetime_string) {
      if (!datetime_string) return '';
      const date = new Date(datetime_string);
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },

    format_date(datetime_string) {
      if (!datetime_string) return '';
      const date = new Date(datetime_string);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (date.toDateString() === today.toDateString()) return 'Today';
      if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    },

    format_time_ago(datetime_string) {
      if (!datetime_string) return '';
      const date = new Date(datetime_string);
      const now = new Date();
      const diff = Math.floor((now - date) / 1000);
      if (diff < 60) return 'Just now';
      if (diff < 3600) return `${Math.floor(diff / 60)}m`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
      if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    },

    shared_item_matches_search(values) {
      const query = this.shared_search_query.trim().toLowerCase();
      if (!query) return true;
      return values.filter(Boolean).join(' ').toLowerCase().includes(query);
    },

    get_file_size_label(file) {
      const bytes = Number(file?.file_size || file?.size || file?.bytes || 0);
      if (!bytes) return '';
      const units = ['B', 'KB', 'MB', 'GB'];
      let size = bytes;
      let unit_index = 0;
      while (size >= 1024 && unit_index < units.length - 1) {
        size /= 1024;
        unit_index += 1;
      }
      return `${size.toFixed(size >= 10 || unit_index === 0 ? 0 : 1)} ${Reflect.get(units, unit_index)}`;
    },

    get_shared_meta(item) {
      return [
        `Shared by ${this.get_sender_name(item.sender)}`,
        this.format_date(item.created_at),
        this.get_file_size_label(item),
      ].filter(Boolean).join(' - ');
    },

    get_message_links(message) {
      const url_regex = /(https?:\/\/[^\s<>"']+)/gi;
      const matches = (message.content || '').match(url_regex) || [];
      return matches.map((raw_url, index) => {
        const url = raw_url.replace(/[),.]+$/, '');
        return ({
          id: `${message.id}-link-${index}`,
          url,
          title: this.get_link_title(url),
          domain: this.get_link_domain(url),
          created_at: message.created_at,
          sender: message.sender,
          message_id: message.id,
          message_key: `msg-${message.id}`,
        });
      });
    },

    get_shared_links() {
      return this.messages.flatMap(message => this.get_message_links(message));
    },

    get_link_domain(url) {
      const link = document.createElement('a');
      link.href = url;
      return link.hostname ? link.hostname.replace(/^www\./, '') : url;
    },

    get_link_title(url) {
      return this.get_link_domain(url) || url;
    },

    open_link(url) {
      if (!url) return;
      window.open(url, '_blank', 'noopener,noreferrer');
    },

    async copy_link(url) {
      if (!url || !navigator.clipboard) return;
      await navigator.clipboard.writeText(url);
    },

    jump_to_message(message_id) {
      const msg_index = this.rendered_stream.filter(item => item.kind === 'msg').findIndex(item => String(item.id) === String(message_id));
      if (msg_index === -1) return;
      this.show_info_panel = window.innerWidth >= 992;
      this.$nextTick(() => {
        const scroll_el = this.$refs.scroll_area;
        if (!scroll_el) return;
        const row_el = Reflect.get(scroll_el.querySelectorAll('.message-row'), msg_index);
        if (!row_el) return;
        row_el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const bubble = row_el.querySelector('.message-bubble');
        if (bubble) {
          bubble.classList.remove('msg-jump-flash');
          void bubble.offsetWidth;
          bubble.classList.add('msg-jump-flash');
          setTimeout(() => bubble.classList.remove('msg-jump-flash'), 1800);
        }
      });
    },

    auto_resize_textarea(event) {
      const textarea = event.target;
      textarea.style.height = 'auto';
      const computed_style = window.getComputedStyle(textarea);
      const max_height = parseInt(computed_style.maxHeight, 10);
      const min_height = parseInt(computed_style.minHeight, 10) || 40;
      const new_height = Math.min(Math.max(textarea.scrollHeight, min_height), max_height);
      textarea.style.height = new_height + 'px';
      this.send_typing_start();
    },

    close_modal() { $('#newConversationModal').modal('hide'); },

    truncate_text(text, length = 30) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },

    is_image_file(file_name) {
      if (!file_name) return false;
      const ext = file_name.split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
    },

    is_video_file(file_name) {
      if (!file_name) return false;
      const ext = file_name.split('.').pop().toLowerCase();
      return ['mp4', 'webm', 'ogg', 'ogv', 'mov'].includes(ext);
    },

    is_audio_file(file_name) {
      if (!file_name) return false;
      const ext = file_name.split('.').pop().toLowerCase();
      return ['mp3', 'wav', 'ogg', 'oga', 'aac', 'm4a', 'flac', 'opus', 'weba'].includes(ext);
    },

    stop_other_media(event) {
      const current = event.target;
      const scroll_el = this.$refs.scroll_area;
      if (!scroll_el) return;
      scroll_el.querySelectorAll('audio, video').forEach(el => {
        if (el !== current && !el.paused) {
          el.pause();
        }
      });
    },

    file_icon_chat(file_name) {
      const ext = (file_name || '').split('.').pop().toLowerCase();
      if (['jpg','jpeg','png','webp','gif','svg'].includes(ext)) return "far fa-file-image text-success";
      if (ext === "pdf") return "far fa-file-pdf text-danger";
      if (['doc','docx'].includes(ext)) return "far fa-file-word text-primary";
      if (['mp4','webm','ogg','ogv','mov'].includes(ext)) return "fas fa-film text-warning";
      if (['mp3','wav','aac','m4a','flac','opus'].includes(ext)) return "fas fa-music text-info";
      return "far fa-file-alt text-secondary";
    },

    preview_chat_file(file) {
      this.selected_preview_file = file;
      this.preview_file_name = this.get_chat_file_name(file);
      this.preview_file_url = this.get_chat_file_url(file);
      this.preview_file_access_level = null;
      this.preview_modal = true;
    },

    get_chat_file_name(file) {
      return file?.file_name || file?.name || file?.filename || 'Attachment';
    },

    get_chat_file_url(file) {
      return file?.file_url || file?.url || file?.presigned_url || file?.download_url || file?.attachment_url || '';
    },

    get_chat_drive_item_id(file) {
      return file?.drive_item_id
        || file?.aic_drive_item_id
        || file?.drive_item?.id
        || file?.drive?.id
        || file?.item_id
        || null;
    },

    acquire_editor_lock(lock_key, ttl_ms = 1500) {
      const now = Date.now();
      const locked_at = Number(Reflect.get(this.editor_open_locks, lock_key) || 0);
      if (locked_at && now - locked_at < ttl_ms) return false;
      Reflect.set(this.editor_open_locks, lock_key, now);
      return true;
    },

    release_editor_lock(lock_key) {
      Reflect.set(this.editor_open_locks, lock_key, 0);
    },

    open_editor_tab(editor_url, tab_key) {
      const editor_tab = window.open(editor_url, tab_key);
      if (!editor_tab) {
        this.$notyf.error('Please allow pop-ups to open this Office file.');
        return false;
      }
      if (editor_tab.focus) editor_tab.focus();
      return true;
    },

    open_chat_office_file(file = this.selected_preview_file) {
      const drive_item_id = this.get_chat_drive_item_id(file);
      if (!drive_item_id) {
        const file_url = this.get_chat_file_url(file);
        if (file_url && !this.open_editor_tab(file_url, '_blank')) return;
        if (!file_url) this.$notyf.error('This attachment does not have a file URL.');
        return;
      }

      const token = get_token();
      if (!token) {
        this.$router.push({ name: 'Login' });
        return;
      }

      const editor_lock_key = `chat_editor_open_${drive_item_id}`;
      const editor_tab_name = `aic_chat_editor_${drive_item_id}`;
      if (!this.acquire_editor_lock(editor_lock_key)) return;

      $.ajax({
        url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/aic_drive/api/open_in_editor_api/`,
        method: 'POST',
        contentType: 'application/json',
        data: JSON.stringify({ drive_item_id }),
        headers: {
          Authorization: `Token ${token}`,
          'X-Page-URL': window.location.href,
        },
        success: (response) => {
          const res = typeof response === 'string' ? JSON.parse(response) : response;
          const editor_url = res?.editor_url || res?.data?.editor_url;
          if (!editor_url) {
            this.$notyf.error('Editor URL was not returned by server.');
            this.release_editor_lock(editor_lock_key);
            return;
          }
          if (!this.open_editor_tab(editor_url, editor_tab_name)) {
            this.release_editor_lock(editor_lock_key);
          }
        },
        error: (xhr) => {
          this.release_editor_lock(editor_lock_key);
          if (xhr.status === 401) {
            this.$router.push({ name: 'Login' });
            return;
          }
          this.$notyf.error(xhr?.responseJSON?.message || `Failed to open editor: HTTP ${xhr.status}`);
        },
      });
    },

    close_the_file_preview() {
      this.preview_modal = false;
      this.preview_file_url = null;
      this.selected_preview_file = null;
    },

    handle_file_select(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.selected_file = file;
      this.revoke_preview_url();
      if (this.is_image_file(file.name)) {
        this.selected_file_preview_url = URL.createObjectURL(file);
      } else {
        this.selected_file_preview_url = null;
      }
    },

    remove_selected_file() {
      this.selected_file = null;
      this.revoke_preview_url();
      if (this.$refs.file_input) this.$refs.file_input.value = '';
    },

    revoke_preview_url() {
      if (this.selected_file_preview_url) {
        URL.revokeObjectURL(this.selected_file_preview_url);
        this.selected_file_preview_url = null;
      }
    },

    get_initials(name) {
      if (!name || name === 'Unknown') return '?';
      const parts = name.trim().split(/\s+/).filter(p => /^[a-zA-Z]/.test(p));
      if (!parts.length) return '?';
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },

    get_avatar_class(name) {
      const palette = ['oc-av-0','oc-av-1','oc-av-2','oc-av-3','oc-av-4','oc-av-5','oc-av-6','oc-av-7','oc-av-8','oc-av-9'];
      if (!name || name === 'Unknown') return 'oc-av-unknown';
      let hash = 0;
      for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
      return palette[Math.abs(hash) % palette.length];
    },

    get_presence_class(entity) {
      const raw = this.get_presence_value(entity);
      if (this.has_presence_marker(raw, ['away', 'idle'])) return 'away';
      if (this.has_presence_marker(raw, ['offline', 'inactive'])) return 'offline';
      if (entity?.is_online || this.has_presence_marker(raw, ['online', 'active'])) return 'online';
      return 'offline';
    },

    get_presence_value(entity) {
      return String(
        entity?.presence
        || entity?.status
        || entity?.availability
        || entity?.user?.presence
        || entity?.user?.status
        || ''
      ).toLowerCase();
    },

    has_presence_marker(raw, markers) {
      return markers.some(marker => raw.includes(marker));
    },

    get_avatar_style(name) {
      const palette = ['#C8102E','#1a73e8','#0f9d58','#e52592','#f29900','#7627bb','#12b5cb','#e65100','#00796b','#4285f4'];
      if (!name || name === 'Unknown') return ({ background: '#9E9890', color: '#fff' });
      let hash = 0;
      for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
      return ({ background: palette[Math.abs(hash) % palette.length], color: '#fff' });
    },

    get_sender_name_by_id(msg_id) {
      const msg = this.messages.find(m => m.id === msg_id);
      if (!msg || !msg.sender) return 'Unknown';
      return `${msg.sender.first_name || ''} ${msg.sender.last_name || ''}`.trim() || msg.sender.username || 'Unknown';
    },

    open_info_panel(tab = 'pinned') {
      this.info_tab        = tab === 'files' ? 'shared' : tab;
      if (this.active_is_group && this.info_tab === 'profile') this.info_tab = 'about';
      if (!this.active_is_group && ['about', 'members'].includes(this.info_tab)) this.info_tab = 'profile';
      this.show_info_panel = true;
      this.close_message_search();
    },

    toggle_info_panel() {
      this.show_info_panel = !this.show_info_panel;
      if (this.show_info_panel) {
        if (this.info_tab === 'files') this.info_tab = 'shared';
        if (this.active_is_group && this.info_tab === 'profile') this.info_tab = 'about';
        if (!this.active_is_group && ['about', 'members'].includes(this.info_tab)) this.info_tab = 'profile';
        this.close_message_search();
      }
    },

    handle_touch_start(item, event) {
      this._touch_moved   = false;
      this._touch_start_x = event.touches[0].clientX;
      this._touch_start_y = event.touches[0].clientY;
      if (this._long_press_timer) clearTimeout(this._long_press_timer);
      this._long_press_timer = setTimeout(() => {
        if (!this._touch_moved) {
          if (navigator.vibrate) navigator.vibrate(40);
          this.mobile_action_item = item;
        }
      }, 500);
    },

    handle_touch_end() {
      if (this._long_press_timer) {
        clearTimeout(this._long_press_timer);
        this._long_press_timer = null;
      }
    },

    handle_touch_move(event) {
      if (this._long_press_timer) {
        const dx = Math.abs(event.touches[0].clientX - this._touch_start_x);
        const dy = Math.abs(event.touches[0].clientY - this._touch_start_y);
        if (dx > 8 || dy > 8) {
          this._touch_moved = true;
          clearTimeout(this._long_press_timer);
          this._long_press_timer = null;
        }
      }
    },

    handle_bubble_click(item) {
      if (this.active_toolbar_key === item._k) this.active_toolbar_key = null;
      if (this.emoji_picker_key === item._k) this.emoji_picker_key = null;
    },

    _close_popups_on_outside(e) {
      if (
        this.emoji_picker_key &&
        !e.target.closest('.emoji-picker-popup') &&
        !e.target.closest('.msg-tb-btn-emoji')
      ) {
        this.emoji_picker_key = null;
      }
      if (
        this.active_toolbar_key &&
        !e.target.closest('.msg-more-menu') &&
        !e.target.closest('.msg-tb-btn-dots')
      ) {
        this.active_toolbar_key = null;
      }
      if (
        this.active_conversation_menu_id &&
        !e.target.closest('.conv-menu') &&
        !e.target.closest('.conv-menu-trigger')
      ) {
        this.active_conversation_menu_id = null;
      }
    },

    sort_conversations(conversations) {
      return [...conversations].sort((a, b) => {
        if (Boolean(a.is_pinned) !== Boolean(b.is_pinned)) return a.is_pinned ? -1 : 1;
        return this.conversation_activity_time(b) - this.conversation_activity_time(a);
      });
    },

    conversation_activity_time(conversation) {
      const timestamp =
        conversation?.last_message?.created_at
        || conversation?.last_message?.updated_at
        || conversation?.updated_at
        || conversation?.created_at;
      const value = timestamp ? new Date(timestamp).getTime() : 0;
      return Number.isNaN(value) ? 0 : value;
    },

    toggle_conversation_menu(conversation_id) {
      this.active_conversation_menu_id =
        this.active_conversation_menu_id === conversation_id ? null : conversation_id;
    },

    toggle_emoji_picker(item, event) {
      event.stopPropagation();
      this.emoji_picker_key = this.emoji_picker_key === item._k ? null : item._k;
    },

    toggle_more_menu(item, event) {
      event.stopPropagation();
      if (this.active_toolbar_key === item._k) {
        this.active_toolbar_key = null;
        return;
      }
      const btn = event.currentTarget;
      const btn_rect = btn.getBoundingClientRect();
      const scroll_el = this.$refs.scroll_area;
      let direction = 'up';
      if (scroll_el) {
        const scroll_rect = scroll_el.getBoundingClientRect();
        const space_above = btn_rect.top - scroll_rect.top;
        if (space_above < 150) direction = 'down';
      }
      this.toolbar_menu_direction = {
        ...this.toolbar_menu_direction,
        [item._k]: direction,
      };
      this.active_toolbar_key = item._k;
    },

    my_reaction_for(item) {
      if (!item.reactions || !item.reactions.length) return null;
      const r = item.reactions.find(rg => rg.reacted_by_me);
      return r ? r.emoji : null;
    },

    async send_reaction(item, emoji, event) {
      if (event) event.stopPropagation();
      if (this.reacting_message_id === item.id) return;

      const token = this.require_token_or_redirect();
      if (!token) return;

      const current = this.my_reaction_for(item);
      const emoji_to_send = current === emoji ? '' : emoji;

      this.reacting_message_id = item.id;
      this.emoji_picker_key = null;

      try {
        const data = await this.one_chat_request('message.react', {
          message_id: item.id,
          conversation_id: this.active_conversation_id,
          reaction: emoji_to_send,
        });

        if (data?.status === 'success' || data?.id || data?.reactions) {
          const updated_data = data.data || data;
          const message_data = updated_data.message || updated_data;
          const reactions = message_data.reactions || updated_data.reactions;
          if (Array.isArray(reactions)) {
            this.patch_message_metadata(item.id, {
              conversation_id: this.active_conversation_id,
              ...message_data,
              reactions,
            });
          }
        }
      } catch {
        this.$notyf.error('Failed to react to message');
      } finally {
        this.reacting_message_id = null;
      }
    },

    set_reply_to(item) {
      const sender_name = this.get_sender_name_by_id(item.id);
      this.reply_to_message = {
        id: item.id,
        sender_name,
        content: item.text || '',
        attachments: item.attachments || [],
      };
      this.$nextTick(() => {
        const ta = this.$el.querySelector('.oc-textarea');
        if (ta) ta.focus();
      });
    },

    clear_reply_to() {
      this.reply_to_message = null;
    },

    // ── Data loading ──────────────────────────────
    async load_initial_data() {
      await this.load_current_user();
      this.connect_chat_socket();
      await Promise.all([
        this.wait_for_chat_socket(),
        this.load_available_users(),
      ]);
      await this.load_conversations();
      this.sync_ic_workbook_portal_conversations();
    },

    load_current_user() {
      const token = this.require_token_or_redirect();
      if (!token) return Promise.resolve();
      return $.ajax({
        url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/get_current_user/`,
        method: 'GET',
        xhrFields: { withCredentials: true },
        headers: { 'Content-Type': 'application/json', ...this._auth_headers(token) },
      }).then(data => {
        if (data?.status === 'success') {
          this.current_user = data.data;
          this.current_user_id = data.data.id;
        } else {
          this.$notyf.error(data?.message || 'Failed to load user');
        }
      }).catch(jq => {
        this.$notyf.error(jq?.responseJSON?.message || `Error loading user: HTTP ${jq.status}`);
      });
    },

    load_conversations(is_silent = false) {
      const token = this.require_token_or_redirect();
      if (!token) return Promise.resolve();
      if (!is_silent) this.conversations_loading = true;
      return this.one_chat_request('conversation.list').then(data => {
        if (data?.status === 'success') {
          this.conversations = (data.data || [])
            .filter(summary => {
              const is_locally_hidden = this.hidden_conversation_ids.includes(summary.id);
              return !is_locally_hidden || summary.last_message;
            })
            .map(summary => {
              if (summary.last_message && this.hidden_conversation_ids.includes(summary.id)) {
                this.hidden_conversation_ids = this.hidden_conversation_ids.filter(id => id !== summary.id);
              }
              const existing = this.conversations.find(c => c.id === summary.id);
              if (!existing?.participants?.length || summary.participants?.length) return summary;
              return ({
                ...summary,
                participants: existing.participants,
                member_count: summary.member_count || existing.member_count,
              });
            });
        }
      }).finally(() => {
        this.conversations_loading = false;
        this.conversations_loaded = true;
      });
    },

    load_conversation_detail(conversation_id) {
      const token = this.require_token_or_redirect();
      if (!token) return Promise.resolve();
      return this.one_chat_request('conversation.detail', { conversation_id }).then(data => {
        if (data?.status === 'success' && data.data?.id) {
          const existing = this.conversations.find(c => c.id === data.data.id) || {};
          this.upsert_conversation({
            ...existing,
            ...data.data,
            member_count: data.data.member_count || existing.member_count,
          });
        }
      }).catch(jq => {
        console.error('Error loading conversation detail:', jq);
      });
    },

    sync_ic_workbook_portal_conversations() {
      const token = this.require_token_or_redirect();
      if (!token) return Promise.resolve();
      if (this._portal_sync_in_flight) return Promise.resolve();
      this._portal_sync_in_flight = true;
      this.portal_syncing = true;
      this.portal_sync_error = '';

      return this.one_chat_request('conversation.sync_ic_workbook_portals', { source: 'ic_workbook' }, 60000).then(data => {
        if (data?.status === 'success' && Array.isArray(data.data)) {
          data.data.forEach(conversation => this.upsert_conversation(conversation));
        } else if (data?.message) {
          this.portal_sync_error = data.message;
        }
      }).catch(jq => {
        this.portal_sync_error = jq?.responseJSON?.message || 'Unable to sync IC Workbook portals';
        console.error('IC Workbook portal chat sync failed:', jq);
      }).finally(() => {
        this._portal_sync_in_flight = false;
        this.portal_syncing = false;
      });
    },

    load_available_users() {
      const token = this.require_token_or_redirect();
      if (!token) return Promise.resolve();
      this.users_loading = true;
      return $.ajax({
        url: `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/aic_drive/get_available_users/`,
        method: 'GET',
        xhrFields: { withCredentials: true },
        headers: { 'Content-Type': 'application/json', ...this._auth_headers(token) },
      }).then(data => {
        this.available_users = data?.status === 'success' ? data.data || [] : [];
      }).catch(jq => {
        console.error('Error loading users:', jq);
        this.available_users = [];
      }).always(() => {
        this.users_loading = false;
      });
    },

    add_group_member(user_id = this.group_member_user_id) {
      if (!user_id || this.group_members_updating) return;
      const token = this.require_token_or_redirect();
      if (!token) return;
      this.group_members_updating = true;

      this.one_chat_request('group.member.add', {
        conversation_id: this.active_conversation_id,
        user_id: parseInt(user_id, 10),
      }).then(data => {
        if (data?.status === 'success') {
          this.upsert_conversation(data.data);
          this.group_member_user_id = "";
          this.group_member_search_query = "";
          this.group_member_add_open = false;
          this.$notyf.success('Member added');
        } else {
          this.$notyf.error(data?.message || 'Failed to add member');
        }
      }).catch(jq => {
        this.$notyf.error(jq?.responseJSON?.message || 'Failed to add member');
      }).finally(() => {
        this.group_members_updating = false;
      });
    },

    confirm_conversation_action(conversation, action) {
      if (!conversation?.id || this.conversation_action_loading) return;
      this.pending_conversation_action = { conversation, action };
      this.active_conversation_menu_id = null;
      $('#conversationActionModal').modal('show');
    },

    conversation_action_title() {
      return this.pending_conversation_action?.action === 'clear' ? 'Clear chat?' : 'Delete chat?';
    },

    conversation_action_body() {
      const action = this.pending_conversation_action?.action;
      const conversation = this.pending_conversation_action?.conversation;
      const name = this.get_conversation_name(conversation);
      if (action === 'clear') {
        return `This clears messages from ${name} for you only. Other people will still keep their copy.`;
      }
      return `This removes ${name} from your chat list for you only. Other people will still keep their copy.`;
    },

    conversation_action_button_label() {
      return this.pending_conversation_action?.action === 'clear' ? 'Clear for me' : 'Delete for me';
    },

    conversation_action_success_message(action) {
      return action === 'clear' ? 'Chat cleared for you' : 'Chat deleted for you';
    },

    handle_conversation_action_success(conversation_id, action) {
      this.apply_conversation_action(conversation_id, action);
      $('#conversationActionModal').modal('hide');
      this.pending_conversation_action = null;
      this.$notyf.success(this.conversation_action_success_message(action));
      window.dispatchEvent(new Event('refresh-unread-chats'));
    },

    async execute_conversation_action() {
      if (!this.pending_conversation_action || this.conversation_action_loading) return;
      const token = this.require_token_or_redirect();
      if (!token) return;

      const { conversation, action } = this.pending_conversation_action;
      this.conversation_action_loading = true;

      try {
        const data = await this.one_chat_request(action === 'clear' ? 'conversation.clear' : 'conversation.delete', {
            conversation_id: conversation.id,
            scope: 'me',
        });

        if (data?.status === 'success') {
          this.handle_conversation_action_success(conversation.id, action);
        } else {
          this.$notyf.error(data?.message || `Failed to ${action} chat`);
        }
      } catch (jq) {
        this.$notyf.error(jq?.responseJSON?.message || `Network error while trying to ${action} chat`);
      } finally {
        this.conversation_action_loading = false;
      }
    },

    apply_conversation_action(conversation_id, action) {
      if (action === 'delete') {
        if (!this.hidden_conversation_ids.includes(conversation_id)) {
          this.hidden_conversation_ids.push(conversation_id);
        }
        this.conversations = this.conversations.filter(conv => conv.id !== conversation_id);
        if (this.active_conversation_id === conversation_id) {
          this.active_conversation_id = null;
          this.messages = [];
          this.pinned_messages = [];
          this.messages_offset = 0;
          this.messages_total = 0;
          this.messages_has_more = false;
          this.show_info_panel = false;
        }
        return;
      }

      const idx = this.conversations.findIndex(conv => conv.id === conversation_id);
      if (idx !== -1) {
        const current = Reflect.get(this.conversations, idx);
        this.conversations.splice(idx, 1, {
          ...current,
          last_message: null,
          unread_count: 0,
          cleared_for_me: true,
        });
      }

      if (this.active_conversation_id === conversation_id) {
        this.messages = [];
        this.messages_offset = 0;
        this.messages_total = 0;
        this.messages_has_more = false;
        this.pinned_messages = [];
        this.show_info_panel = false;
      }
    },

    confirm_remove_group_member(member) {
      if (!member?.id || member.is_me || this.group_members_updating) return;
      this.pending_remove_member = member;
      $('#removeGroupMemberModal').modal('show');
    },

    remove_group_member() {
      const member = this.pending_remove_member;
      if (!member?.id || member.is_me || this.group_members_updating) return;
      const token = this.require_token_or_redirect();
      if (!token) return;
      this.group_members_updating = true;

      this.one_chat_request('group.member.remove', {
          conversation_id: this.active_conversation_id,
          user_id: member.id,
      }).then(data => {
        if (data?.status === 'success') {
          this.upsert_conversation(data.data);
          $('#removeGroupMemberModal').modal('hide');
          this.pending_remove_member = null;
          this.$notyf.success('Member removed');
        } else {
          this.$notyf.error(data?.message || 'Failed to remove member');
        }
      }).catch(jq => {
        this.$notyf.error(jq?.responseJSON?.message || 'Failed to remove member');
      }).finally(() => {
        this.group_members_updating = false;
      });
    },

    select_conversation(conversation_id) {
      this.active_conversation_menu_id = null;
      this.stop_active_message_refresh();
      this.active_conversation_id = conversation_id;
      this.messages = [];
      this.messages_offset = 0;
      this.messages_has_more = false;
      this.messages_total = 0;
      this.loading_more_messages = false;
      this.active_message_refresh_in_flight = false;
      this._is_adjusting_scroll = false;
      this.pinned_messages = [];
      this.show_pinned_panel = false;
      this.user_scrolled_up = false;
      this.new_messages_banner = false;
      this.show_info_panel = false;
      this.info_tab = 'profile';
      this.active_toolbar_key = null;
      this.mobile_action_item = null;
      this.emoji_picker_key = null;
      this.reply_to_message = null;
      this.show_message_search  = false;
      this.group_member_list_query = "";
      this.message_search_query = '';
      this.message_search_index = 0;
      this.show_find_panel = false;
      this.search_filter_person = '';
      this.search_filter_date = '';
      this.conversation_participants = [];
      this.group_member_user_id = "";
      this.group_member_search_query = "";
      this.group_member_add_open = false;
      this._messages_initialized = false;
      this.cancel_edit();
      this.load_conversation_detail(conversation_id);
      this.load_messages(conversation_id);
      this.load_pinned_messages(conversation_id);
      if (this.is_chat_socket_open()) {
        this.chat_socket.subscribe(conversation_id);
      }
    },

    load_messages(conversation_id, load_more = false) {
      const token = this.require_token_or_redirect();
      if (!token) return Promise.resolve();

      const offset = load_more ? this.messages_offset : 0;
      if (!load_more) this.messages_loading = true;
      if (load_more) this.loading_more_messages = true;

      return this.one_chat_request('conversation.messages', { conversation_id, limit: 50, offset }).then(data => {
        if (data?.status !== 'success') return;

        const { messages: fetched = [], has_more, total } = data.data;

        if (!load_more) return this._handle_initial_load(fetched, has_more, total, conversation_id);
        if (load_more) return this._handle_load_more(fetched, has_more, total);
      }).finally(() => {
        this.messages_loading = false;
        this.loading_more_messages = false;
      });
    },

    _handle_initial_load(fetched, has_more, total, conversation_id) {
      const el = this.$refs.scroll_area;
      if (el) el.style.overflow = 'hidden';

      this.messages = fetched;
      this.messages_offset = fetched.length;
      this.messages_has_more = has_more;
      this.messages_total = total;

      this.$nextTick(() => {
        const area = this.$refs.scroll_area;
        if (area) {
          area.scrollTop = area.scrollHeight;
          area.style.overflow = '';
        }
        this.attach_scroll_listener();
        this.mark_as_read(conversation_id);
        this._messages_initialized = true;
        if (String(conversation_id) === String(this.active_conversation_id)) {
          this.start_active_message_refresh();
        }
      });
    },

    _handle_load_more(fetched, has_more, total) {
      this.messages_has_more = has_more;
      this.messages_total = total;
      if (!fetched.length) return;

      const el = this.$refs.scroll_area;

      let anchor_el = null;
      let anchor_offset = 0;
      if (el) {
        const container_rect = el.getBoundingClientRect();
        for (const row of el.querySelectorAll('.message-row')) {
          if (row.getBoundingClientRect().bottom > container_rect.top) {
            anchor_el = row;
            anchor_offset = row.getBoundingClientRect().top - container_rect.top;
            break;
          }
        }
      }

      const existing_ids = new Set(this.messages.map(m => m.id));
      const unique_older = fetched.filter(m => !existing_ids.has(m.id));
      if (!unique_older.length) return;

      this.messages = [
        ...unique_older,
        ...this.messages
      ];
      this.messages_offset += unique_older.length;

      this.$nextTick(() => {
        if (!el || !anchor_el) return;
        const container_rect = el.getBoundingClientRect();
        const correction = anchor_el.getBoundingClientRect().top - container_rect.top - anchor_offset;
        this._is_adjusting_scroll = true;
        el.scrollTop += correction;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => { this._is_adjusting_scroll = false; });
        });
      });
    },

    load_more_messages() {
      if (this.loading_more_messages || !this.messages_has_more) return;
      this.load_messages(this.active_conversation_id, true);
    },

    async send() {
      if ((!this.draft && !this.selected_file) || this.sending_message) return;
      const token = this.require_token_or_redirect();
      if (!token) return;

      if (!this.is_chat_socket_open()) {
        this.$notyf.error('WebSocket is not connected. Please try again.');
        return;
      }

      this.sending_message = true;
      const backup = this.create_send_backup();
      const pending_message = this.create_pending_message(backup);
      this.apply_pending_message(pending_message);
      let payload = null;

      try {
        payload = await this.create_send_payload();
      } catch (error) {
        this.remove_pending_message(backup.client_message_id);
        this.handle_send_payload_error(error);
        return;
      }

      this.clear_send_input();
      this.reset_textarea_height();

      const sent = this.chat_socket.send_message(
        this.active_conversation_id,
        backup.draft,
        {
          ...payload,
          client_message_id: backup.client_message_id,
        }
      );
      this.finish_socket_send(sent, backup);
    },

    create_send_backup() {
      return ({
        client_message_id: `local-${Date.now()}`,
        draft: this.draft,
        reply_to_message: this.reply_to_message,
        selected_file: this.selected_file,
        selected_file_preview_url: this.selected_file_preview_url,
      });
    },

    async create_send_payload() {
      const payload = {
        reply_to_id: this.reply_to_message?.id || null,
      };
      if (this.selected_file) {
        const file_payload = await this.selected_file_payload(this.selected_file);
        Object.assign(payload, file_payload);
      }
      return payload;
    },

    handle_send_payload_error(error) {
      this.$notyf.error(error?.message || 'Could not read selected file.');
      this.sending_message = false;
    },

    clear_send_input() {
      this.draft = "";
      this.selected_file = null;
      this.reply_to_message = null;
      this.selected_file_preview_url = null;
    },

    reset_textarea_height() {
      this.$nextTick(() => {
        const textarea = this.$el.querySelector('.oc-textarea');
        if (textarea) textarea.style.height = 'auto';
      });
    },

    finish_socket_send(sent, backup) {
      if (!sent) {
        this.mark_pending_message_failed(backup.client_message_id);
        this.draft = backup.draft;
        this.reply_to_message = backup.reply_to_message;
        this.selected_file = backup.selected_file;
        this.selected_file_preview_url = backup.selected_file_preview_url;
        this.$notyf.error('WebSocket is not connected. Please try again.');
      }
      this.sending_message = false;
    },

    create_pending_message(backup) {
      return ({
        id: backup.client_message_id,
        client_message_id: backup.client_message_id,
        conversation_id: this.active_conversation_id,
        content: backup.draft || '',
        reply_to: backup.reply_to_message,
        attachments: backup.selected_file ? [this.create_pending_attachment(backup)] : [],
        sender: this.current_user || { id: this.current_user_id },
        created_at: new Date().toISOString(),
        pending: true,
      });
    },

    create_pending_attachment(backup) {
      const file = backup.selected_file;
      return ({
        id: `${backup.client_message_id}-attachment`,
        local_id: `${backup.client_message_id}-attachment`,
        name: file.name,
        file_name: file.name,
        file_type: file.type || 'application/octet-stream',
        file_size: file.size || 0,
        pending: true,
        file_url: this.is_image_file(file.name) ? backup.selected_file_preview_url : '',
      });
    },

    apply_pending_message(message) {
      this.messages.push(message);
      this.messages_offset += 1;
      if (this.active_conversation) {
        this.active_conversation.last_message = message;
        this.active_conversation.updated_at = message.created_at;
      }
      this.user_scrolled_up = false;
      this.new_messages_banner = false;
      this.scroll_to_bottom(true);
    },

    mark_pending_message_failed(client_message_id) {
      this.messages = this.messages.map(message => (
        String(message.client_message_id || message.id) === String(client_message_id)
          ? {
              ...message,
              pending: false,
              failed: true,
              attachments: (message.attachments || []).map(file => ({ ...file, pending: false })),
            }
          : message
      ));
    },

    remove_pending_message(client_message_id) {
      const before = this.messages.length;
      this.messages = this.messages.filter(message => (
        String(message.client_message_id || message.id) !== String(client_message_id)
      ));
      if (this.messages.length !== before) {
        this.messages_offset = Math.max(0, this.messages_offset - (before - this.messages.length));
      }
    },

    selected_file_payload(file) {
      if (file.size > MAX_SOCKET_ATTACHMENT_BYTES) {
        const limit_mb = Math.floor(MAX_SOCKET_ATTACHMENT_BYTES / (1024 * 1024));
        return Promise.reject(new Error(`Attachments must be ${limit_mb}MB or smaller.`));
      }

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = String(reader.result || '');
          const file_base64 = result.includes(',')
            ? result.split(',').pop()
            : result;
          resolve({
            file_base64,
            file_name: file.name,
            file_type: file.type || 'application/octet-stream',
            file_size: file.size || 0,
          });
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
    },

    mark_as_read(conversation_id) {
      if (!this.messages.length) return;
      const has_incoming = this.messages.some(m => m.sender && m.sender.id !== this.current_user_id);
      if (!has_incoming) return;
      const token = this.require_token_or_redirect();
      if (!token) return;
      const last_message = this.messages.at(-1);

      if (this.is_chat_socket_open()) {
        this.chat_socket.mark_read(conversation_id, last_message.id);
        const conv = this.conversations.find(c => c.id === conversation_id);
        if (conv) conv.unread_count = 0;
        window.dispatchEvent(new Event('refresh-unread-chats'));
        return;
      }

      this.one_chat_request('message.read', {
        conversation_id,
        last_message_id: last_message.id,
      }).then(data => {
        if (data?.status === 'success') {
          const conv = this.conversations.find(c => c.id === conversation_id);
          if (conv) conv.unread_count = 0;
          window.dispatchEvent(new Event('refresh-unread-chats'));
        }
      }).catch(jq => console.error('Error marking as read:', jq));
    },

    mark_conversation_read(conversation) {
      this.active_conversation_menu_id = null;
      if (!conversation) return;
      if (conversation.id === this.active_conversation_id) {
        this.mark_as_read(conversation.id);
        return;
      }
      const token = this.require_token_or_redirect();
      const last_message_id = conversation.last_message?.id;
      if (!token || !last_message_id) return;
      this.one_chat_request('message.read', {
        conversation_id: conversation.id,
        last_message_id,
      }).then(data => {
        if (data?.status === 'success') {
          conversation.unread_count = 0;
          window.dispatchEvent(new Event('refresh-unread-chats'));
        }
      }).catch(jq => console.error('Error marking conversation as read:', jq));
    },

    start_new_conversation() {
      if (!this.new_conversation_user_id) {
        this.$notyf.info('Please select a user');
        return;
      }
      const token = this.require_token_or_redirect();
      if (!token) return;
      this.starting_conversation = true;

      this.one_chat_request('conversation.start_direct', {
        user_id: parseInt(this.new_conversation_user_id),
        initial_message: this.new_conversation_message || '',
      }).then(data => {
        if (data?.status === 'success') {
          const conversation = data.data.conversation;
          if (!conversation.participants || conversation.participants.length === 0) {
            const other_user = this.available_users.find(
              u => u.id === parseInt(this.new_conversation_user_id, 10)
            );
            if (other_user) {
              conversation.participants = [
                { id: this.current_user_id, ...this.current_user },
                {
                  id: other_user.id,
                  first_name: other_user.first_name,
                  last_name: other_user.last_name,
                  username: other_user.username,
                  avatar: other_user.avatar
                },
              ];
            }
          }
          this.upsert_conversation(conversation);
          this.$notyf.success('Conversation started successfully');
          this.select_conversation(conversation.id);
          this.close_new_conversation_modal();
        } else {
          this.$notyf.error(data?.message || 'Failed to start conversation');
        }
      }).catch(jq => {
        this.$notyf.error(jq?.responseJSON?.message || `Error starting conversation: HTTP ${jq.status}`);
      }).finally(() => {
        this.starting_conversation = false;
      });
    },

    start_new_group_conversation() {
      const group_name = this.new_group_name.trim();
      if (!group_name) {
        this.$notyf.info('Please enter a group name');
        return;
      }
      if (this.new_group_user_ids.length < 1) {
        this.$notyf.info('Please select at least one member');
        return;
      }

      const token = this.require_token_or_redirect();
      if (!token) return;

      this.starting_group = true;
      const member_ids = [...new Set([
        ...this.new_group_user_ids.map(id => parseInt(id, 10)).filter(Boolean),
        parseInt(this.current_user_id, 10),
      ].filter(Boolean))];

      this.one_chat_request('conversation.start_group', {
        name: group_name,
        user_ids: member_ids,
        initial_message: this.new_conversation_message || '',
      }).then(data => {
        if (data?.status === 'success') {
          const conversation = data.data?.conversation || data.data;
          if (conversation?.id) {
            this.upsert_conversation({
              ...conversation,
              name: conversation.name || group_name,
              is_group: true,
            });
            this.$notyf.success('Group created successfully');
            this.select_conversation(conversation.id);
            this.close_new_conversation_modal();
          } else {
            this.$notyf.error('Failed to create group');
          }
        } else {
          this.$notyf.error(this.extract_api_error_message(data, 'Failed to create group'));
        }
      }).catch(jq => {
        this.$notyf.error(
          this.extract_api_error_message(
            jq?.responseJSON,
            `Error creating group: HTTP ${jq.status}`
          )
        );
      }).finally(() => {
        this.starting_group = false;
      });
    },

    extract_api_error_message(response, fallback = 'Request failed') {
      const errors = response?.errors;
      if (errors && typeof errors === 'object') {
        const first_error = Object.values(errors)[0];
        if (Array.isArray(first_error)) return first_error.join(' ');
        if (first_error) return String(first_error);
      }

      return response?.message || fallback;
    },

    start_edit(item) {
      this.editing_message_key = item._k;
      this.edit_draft          = item.text || '';
      this.$nextTick(() => {
        const ta = this.$el.querySelector('.edit-area textarea');
        if (ta) {
          ta.style.height = 'auto';
          ta.style.height = Math.min(ta.scrollHeight, 200) + 'px';
          ta.focus();
          ta.setSelectionRange(ta.value.length, ta.value.length);
        }
      });
    },

    cancel_edit() {
      this.editing_message_key = null;
      this.edit_draft          = '';
    },

    async submit_edit(item) {
      if (!this.edit_draft.trim() || this.saving_edit) return;
      const token = this.require_token_or_redirect();
      if (!token) return;
      this.saving_edit = true;
      try {
        const res = await this.one_chat_request('message.edit', {
          message_id: item.id,
          conversation_id: this.active_conversation_id,
          content: this.edit_draft.trim(),
        });
        if (res.status === 'success' || res.id) {
          const updated_data = res.data || res;
          this.patch_message_metadata(item.id, {
            conversation_id: this.active_conversation_id,
            content: updated_data.content || this.edit_draft.trim(),
            edited_at: updated_data.edited_at || new Date().toISOString(),
            ...updated_data,
          });
          this.cancel_edit();
          this.$notyf.success('Message updated');
        }
      } catch (e) {
        this.$notyf.error('Failed to edit');
      } finally {
        this.saving_edit = false;
      }
    },

    confirm_delete(item, scope = 'me') {
      this.pending_delete_item = item;
      this.pending_delete_scope = scope;
      $('#deleteMessageModal').modal('show');
    },

    delete_modal_title() {
      return this.pending_delete_scope === 'everyone' ? 'Delete for everyone?' : 'Delete for me?';
    },

    delete_modal_text() {
      if (this.pending_delete_scope === 'everyone') {
        return 'This removes the message for everyone in the conversation.';
      }
      return 'This removes the message from your chat only. Other people will still see it.';
    },

    delete_modal_button_label() {
      return this.pending_delete_scope === 'everyone' ? 'Delete for everyone' : 'Delete for me';
    },

    async execute_delete() {
      if (!this.pending_delete_item || this.deleting_message) return;
      const token = this.require_token_or_redirect();
      if (!token) return;
      this.deleting_message = true;
      try {
        const data = await this.one_chat_request('message.delete', {
          message_id: this.pending_delete_item.id,
          delete_for_everyone: this.pending_delete_scope === 'everyone',
          scope: this.pending_delete_scope,
        });
        if (data?.status === 'success') {
          this.messages = this.messages.filter(m => m.id !== this.pending_delete_item.id);
          this.messages_offset = Math.max(0, this.messages_offset - 1);
          this.pinned_messages = this.pinned_messages.filter(m => m.id !== this.pending_delete_item.id);
          $('#deleteMessageModal').modal('hide');
          this.pending_delete_item = null;
          this.pending_delete_scope = 'me';
        } else {
          this.$notyf.error(data?.message || 'Failed to delete message');
        }
      } catch (jq) {
        this.$notyf.error(jq?.responseJSON?.message || 'Network error while deleting');
      } finally {
        this.deleting_message = false;
      }
    },

    open_read_receipts(item) {
      if (!this.active_is_group || !item?.read_by?.length) return;
      this.read_receipt_item = item;
      $('#readReceiptsModal').modal('show');
    },

    async toggle_pin(item) {
      if (this.pinning_in_progress) return;
      const token = this.require_token_or_redirect();
      if (!token) return;
      this.pinning_in_progress = true;
      const next_pin_value = !item.is_pinned;
      try {
        const response_data = await this.one_chat_request('message.pin', {
          message_id: item.id,
          conversation_id: this.active_conversation_id,
          pin: next_pin_value,
        });
        if (response_data && (response_data.status === 'success' || response_data.id)) {
          const updated_data = response_data.data || response_data;
          const message_data = updated_data.message || updated_data;
          this.apply_pin_update(item.id, next_pin_value, message_data);
        }
      } catch (error_obj) {
        if (error_obj.status === 200 || error_obj.status === 201) {
          this.apply_pin_update(item.id, next_pin_value);
        } else {
          this.$notyf.error(error_obj?.responseJSON?.message || 'network_error_while_pinning');
        }
      } finally {
        this.pinning_in_progress = false;
      }
    },

    apply_pin_update(message_id, is_pinned, message_data = {}) {
      const updated = this.patch_message_metadata(message_id, {
        conversation_id: this.active_conversation_id,
        ...message_data,
        is_pinned,
      });

      if (is_pinned) {
        this.sync_pinned_message_from_update(updated || {
          ...message_data,
          id: message_id,
          is_pinned: true,
        });
        return;
      }

      this.remove_deleted_message_from_pinned(message_id);
    },

    toggle_pin_by_msg(msg) {
      this.toggle_pin({ id: msg.id, is_pinned: true, _k: `msg-${msg.id}` });
    },

    async load_pinned_messages(conversation_id) {
      const token = this.require_token_or_redirect();
      if (!token) return;
      try {
        const data = await this.one_chat_request('message.pinned.list', { conversation_id });
        if (data?.status === 'success') this.pinned_messages = data.data || [];
      } catch (jq) {
        console.error('Failed to load pinned messages:', jq);
      }
    },

    build_conversation_participants() {
      const seen = new Set();
      const list = [];
      for (const msg of this.messages) {
        const id = msg.sender?.id;
        if (!id || seen.has(id)) continue;
        seen.add(id);
        const name =
          `${msg.sender.first_name || ''} ${msg.sender.last_name || ''}`.trim()
          || msg.sender.username
          || 'Unknown';
        list.push({ id: String(id), name });
      }
      this.conversation_participants = list;
    },

    toggle_find_panel() {
      this.show_find_panel = !this.show_find_panel;
      if (this.show_find_panel) {
        this.build_conversation_participants();
      }
    },

    on_search_input() {
      this.message_search_index = 0;
    },

    jump_to_result(idx) {
      this.message_search_index = idx;
      const is_mobile = window.innerWidth < 992;
      if (is_mobile) {
        this.show_find_panel = false;
        this.show_message_search  = false;
        this.search_filter_person = '';
        this.search_filter_date   = '';
        setTimeout(() => this.jump_to_search_result(), 50);
      } else {
        this.jump_to_search_result();
      }
    },

    get_result_sender_name(key) {
      const item = this.rendered_stream.find(i => i._k === key);
      if (!item) return 'Unknown';
      return this.get_sender_name_by_id(item.id);
    },

    get_result_time(key) {
      const item = this.rendered_stream.find(i => i._k === key);
      return item?.timestamp || '';
    },

    get_result_text(key) {
      const item = this.rendered_stream.find(i => i._k === key);
      return item?.text || '';
    },

    get_result_has_attachment(key) {
      const item = this.rendered_stream.find(i => i._k === key);
      return (item?.attachments?.length ?? 0) > 0;
    },

    toggle_message_search() {
      this.show_message_search = !this.show_message_search;
      if (this.show_message_search) {
        this.show_info_panel = false;
        this.build_conversation_participants();
        this.$nextTick(() => {
          const input = this.$refs.msg_search_input;
          if (input) input.focus();
        });
      } else {
        this.message_search_query = '';
        this.message_search_index = 0;
        this.show_find_panel = false;
        this.search_filter_person = '';
        this.search_filter_date = '';
      }
    },

    clear_message_search() {
      this.close_message_search();
    },

    close_message_search() {
      this.show_message_search = false;
      this.message_search_query = '';
      this.message_search_index = 0;
      this.show_find_panel = false;
      this.search_filter_person = '';
      this.search_filter_date = '';
    },

    next_search_result() {
      if (!this.message_search_matches.length) return;
      this.message_search_index =
        (this.message_search_index + 1) % this.message_search_matches.length;
      this.jump_to_result(this.message_search_index);
    },

    prev_search_result() {
      if (!this.message_search_matches.length) return;
      this.message_search_index =
        (this.message_search_index - 1 + this.message_search_matches.length) %
        this.message_search_matches.length;
      this.jump_to_result(this.message_search_index);
    },

    jump_to_search_result() {
      const target_key = this.message_search_matches[this.message_search_index];
      if (!target_key) return;
      this.$nextTick(() => {
        const scroll_el = this.$refs.scroll_area;
        if (!scroll_el) return;
        const rows = scroll_el.querySelectorAll('.message-row');
        const stream_msgs = this.rendered_stream.filter(i => i.kind === 'msg');
        const row_index = stream_msgs.findIndex(i => i._k === target_key);
        const row_el = Reflect.get(rows, row_index);
        if (!row_el) return;
        row_el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const bubble = row_el.querySelector('.message-bubble');
        if (bubble) {
          bubble.classList.remove('msg-jump-flash');
          void bubble.offsetWidth;
          bubble.classList.add('msg-jump-flash');
          setTimeout(() => bubble.classList.remove('msg-jump-flash'), 1800);
        }
      });
    },

    highlight_text(text) {
      if (!text) return '';
      const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
      const q = this.message_search_query.trim();
      if (!q) return escaped;
      const q_lower = q.toLowerCase();
      const src = escaped.toLowerCase();
      let result = '';
      let cursor = 0;
      while (cursor < escaped.length) {
        const match_pos = src.indexOf(q_lower, cursor);
        if (match_pos === -1) {
          result += escaped.slice(cursor);
          break;
        }
        result += escaped.slice(cursor, match_pos);
        const matched_slice = escaped.slice(match_pos, match_pos + q_lower.length);
        result += `<mark class="msg-search-highlight">${matched_slice}</mark>`;
        cursor = match_pos + q_lower.length;
      }
      return result;
    },
  },
};
